// ============================================================================
// AI API SERVICE - authenticated gateway with provider provenance
// ============================================================================
// Production calls go through the authenticated backend gateway. In its
// default mode the backend uses OpenRouter with multi-key rotation; in direct
// mode it can attempt a server-owned Bedrock route for mapped Anthropic models
// and fall back to OpenRouter without exposing AWS credentials to the browser.
// Explicit browser-direct OpenRouter remains a local-development escape only.
//
// Public function signatures are UNCHANGED so existing call sites keep working:
//   - callOpenRouter({ apiKey, model, messages, ... })            → Promise<string>
//   - callOpenRouterVision({ apiKey, model, prompt, images, ... })→ Promise<string>
//   - callOpenRouterStreaming(...)                                → Promise<string>
//   (callOpenRouterVisionStreaming was removed - use callOpenRouterVision instead)
//   - callEmbedding({ input, model, apiKey })                     → Promise<number[] | number[][]>
//
// The `apiKey` parameter is now optional - if not provided, the multi-key
// manager picks the next available key from sessionStorage.
// ============================================================================

import { OPENROUTER_API_URL } from '../config/brand';
import { DEFAULT_TTS_VOICE, resolveTtsVoiceForModel } from '../config/tts-catalog';
import { SecurityUtils } from './security';
import { PipelineMonitor } from './pipeline-monitor';
import {
    resolveModelRouteForTask,
    resolveUnavailableModelLane,
    pickModelForTask,
} from './model-selector';
import {
    OPENROUTER_PROVIDER,
    getNextOpenRouterKey,
    markKeyRateLimited,
    getOpenRouterKeys,
    // Legacy compat exports - old code might still import these
    PROVIDERS,
    PROVIDER_BY_ID,
    inferProviderForModel,
    getKeyForProvider,
    // AI-4 (audit 2026-04-21): pilot-day kill switch: fails every low-level
    // AI call fast + friendly when `qm_disable_ai=true` is set.
    assertAiEnabled,
    // WO-C / TS-014: 'backend' sends every call through /api/ai/*; browser
    // modes are explicit local-dev only.
    getAiGatewayMode,
    assertBrowserOpenRouterAllowed,
} from './ai-providers';
import {
    beginEvalAiLogicalStage,
    evalAiAttemptLimit,
    isEvalAiSingleAttemptPolicyActive,
    suppressEvalAiRetry,
    withEvalAiPaidRequest,
} from './eval-ai-single-attempt-policy';
import {
    governedModelRouteForSignalV1,
    governedSupportRouteForSignalV1,
    recordGovernedRoutingReceiptV1,
} from '../quote-engine/governed-model-route-context';
import { withAiGatewaySlot } from './ai-gateway-limiter';

// ─── AI Gateway routing helpers (WO-C) ──────────────────────────────────
// When gateway mode is 'backend' (the default) the frontend never touches
// OpenRouter directly. Every call goes to POST /api/ai/chat(-with-tools,
// /stream, /vision) with a JWT. The backend attaches OPENROUTER_API_KEY,
// enforces per-tenant spend policy, and writes the usage ledger row.
//
// A pipelineId/stageName pair from the caller maps to a `feature` label for
// the ledger. Feature labels are REQUIRED on every call; missing → 400.
function _pipelineToFeature(pipelineId, stageName) {
    const raw = (pipelineId || stageName || 'other').toString().trim();
    // Normalise to snake_case-ish label. Keep alphanumerics + underscores.
    const label = raw
        .replace(/[^a-zA-Z0-9_-]+/g, '_')
        .replace(/-/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_+|_+$/g, '')
        .toLowerCase();
    return label || 'other';
}

function _isBackendGatewayActive() {
    try {
        const mode = getAiGatewayMode();
        return mode === 'backend' || mode === 'hybrid';
    } catch { return true; /* default to backend if resolver fails */ }
}

function validateModel(model) {
    if (!model) {
        throw new Error('AI model not configured. Please set a model in Settings.');
    }
}

function _governedRouteForGateway(signal, requestedModel) {
    const route = governedModelRouteForSignalV1(signal);
    if (!route) return null;
    if (requestedModel !== route.model) {
        const error = new Error(`Governed Quote Engine route rejected selector drift (${requestedModel} != ${route.model}).`);
        error.name = 'GovernedModelRouteMismatchError';
        error.code = 'QM_GOVERNED_MODEL_ROUTE_MISMATCH';
        throw error;
    }
    if (!_isBackendGatewayActive()) {
        const error = new Error('Governed Quote Engine routes require the authenticated backend gateway.');
        error.name = 'GovernedModelRouteUnavailableError';
        error.code = 'QM_GOVERNED_MODEL_ROUTE_BACKEND_REQUIRED';
        throw error;
    }
    return route;
}

function _governedGatewayFields(route) {
    if (!route) return {};
    return {
        governedModelRoute: route,
        provider: route.provider,
        reasoningEffort: route.reasoningEffort,
        // Reuse the already-proven gateway single-attempt wire contract. The
        // backend independently derives the same constraint from the route.
        evaluationSingleAttempt: true,
    };
}

function _governedSupportRouteForGateway(signal, operation) {
    const route = governedSupportRouteForSignalV1(signal, operation);
    if (!route) return null;
    if (!_isBackendGatewayActive()) {
        const error = new Error('Governed Quote Engine support roles require the authenticated backend gateway.');
        error.name = 'GovernedModelRouteUnavailableError';
        error.code = 'QM_GOVERNED_MODEL_ROUTE_BACKEND_REQUIRED';
        throw error;
    }
    return route;
}

function _governedSupportGatewayFields(route) {
    if (!route) return {};
    return {
        governedSupportRoute: route,
        provider: route.provider,
        reasoningEffort: route.reasoningEffort,
        evaluationSingleAttempt: true,
    };
}

function _bindGovernedRoutingReceipt(route, signal, receipt, boundary) {
    if (!route) return;
    if (!receipt) {
        const error = new Error(`Governed Quote Engine response had no routing receipt at ${boundary}.`);
        error.name = 'GovernedRoutingReceiptMissingError';
        error.code = 'QM_GOVERNED_ROUTING_RECEIPT_MISSING';
        error.route = boundary;
        throw error;
    }
    recordGovernedRoutingReceiptV1(signal, receipt);
}

function _rejectUnsupportedGovernedSubcall(signal, kind) {
    const route = governedModelRouteForSignalV1(signal);
    if (!route) return;
    const error = new Error(
        `Governed Quote Engine stage ${route.stage} cannot issue an unbound ${kind} provider call.`,
    );
    error.name = 'GovernedModelSubcallUnsupportedError';
    error.code = 'QM_GOVERNED_MODEL_SUBCALL_UNBOUND';
    error.stage = route.stage;
    error.kind = kind;
    throw error;
}

function buildModelRouteAudit(task, model, fallbackTask, availabilityRoute = null, modelRouteAttempt = null) {
    const auditTask = task || fallbackTask;
    if (!auditTask) return null;
    const hasExplicitAttempt = !!(modelRouteAttempt && typeof modelRouteAttempt === 'object');
    const attemptedModel = String(model || modelRouteAttempt?.attemptedModel || '').trim();
    try {
        const route = resolveModelRouteForTask(
            auditTask,
            availabilityRoute?.requestedModel || model,
        );
        const availabilityRerouted = !!availabilityRoute?.availabilityRerouted
            || !!route.availabilityRerouted;
        const availabilityWarnings = availabilityRoute?.availabilityWarning
            ? [availabilityRoute.availabilityWarning]
            : route.availabilityWarnings;
        const effectiveModel = attemptedModel || route.effectiveModel;
        const runtimeModelDiffersFromConfiguredRoute = !!(
            attemptedModel
            && route.effectiveModel
            && attemptedModel !== route.effectiveModel
        );
        return {
            taskName: route.taskName,
            requestedModel: availabilityRerouted
                ? (availabilityRoute?.requestedModel || route.requestedModel)
                : route.requestedModel,
            requestedSource: route.requestedSource,
            // The API sends `model`, not the freshly re-resolved recommendation.
            // Keep configuredModel alongside it for comparison, but never claim a
            // protected recommendation ran when the actual request attempted a
            // transient fallback (or another caller-supplied runtime model).
            effectiveModel,
            configuredModel: route.effectiveModel,
            routeSource: hasExplicitAttempt
                ? (modelRouteAttempt.source || 'explicit runtime attempt')
                : (availabilityRerouted
                    ? 'availability lane'
                    : (runtimeModelDiffersFromConfiguredRoute ? 'caller-supplied runtime model' : route.routeSource)),
            configuredRouteSource: route.routeSource,
            recommendationModel: route.recommendationModel,
            strongRecommendation: route.strongRecommendation,
            strongRecommendationBypassed: route.strongRecommendationBypassed,
            overrideApplied: route.overrideApplied,
            availabilityRerouted,
            availabilityWarnings,
            capabilityRerouted: route.capabilityRerouted,
            capabilityWarnings: route.capabilityWarnings,
            runtimeModelDiffersFromConfiguredRoute,
            attemptedModel: attemptedModel || null,
            requestedAttemptedModel: modelRouteAttempt?.attemptedModel || attemptedModel || null,
            attemptNumber: modelRouteAttempt?.attemptNumber ?? 1,
            attemptSource: modelRouteAttempt?.source || null,
            fallbackApplied: modelRouteAttempt?.fallbackApplied === true,
            fallbackFromModel: modelRouteAttempt?.fallbackFromModel || null,
            fallbackToModel: modelRouteAttempt?.fallbackApplied ? (attemptedModel || modelRouteAttempt?.fallbackToModel || null) : null,
            fallbackAttempt: modelRouteAttempt?.fallbackAttempt ?? null,
            fallbackSource: modelRouteAttempt?.fallbackSource || null,
        };
    } catch {
        return {
            taskName: auditTask,
            effectiveModel: attemptedModel,
            attemptedModel: attemptedModel || null,
            routeSource: modelRouteAttempt?.source || 'unavailable',
            attemptNumber: modelRouteAttempt?.attemptNumber ?? 1,
            attemptSource: modelRouteAttempt?.source || null,
            fallbackApplied: modelRouteAttempt?.fallbackApplied === true,
            fallbackFromModel: modelRouteAttempt?.fallbackFromModel || null,
            fallbackToModel: modelRouteAttempt?.fallbackApplied ? (attemptedModel || modelRouteAttempt?.fallbackToModel || null) : null,
            fallbackAttempt: modelRouteAttempt?.fallbackAttempt ?? null,
            fallbackSource: modelRouteAttempt?.fallbackSource || null,
        };
    }
}

function buildModelRouteLedgerMetadata(modelRoute) {
    return modelRoute ? { modelRoute } : undefined;
}

function _decodeProviderAuditHeader(value) {
    if (!value || typeof value !== 'string') return null;
    const decoded = (() => {
        try { return decodeURIComponent(value); } catch { return value; }
    })();
    for (const candidate of [value, decoded]) {
        try {
            const parsed = JSON.parse(candidate);
            if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) return parsed;
        } catch { /* try the next representation */ }
    }
    return null;
}

function _providerAuditFromError(error, defaults = {}) {
    const { provider = null, source = 'backend-gateway' } = defaults;
    const body = error?.body && typeof error.body === 'object' ? error.body : {};
    const providerRoute = error?.qmProviderRoute
        || body.providerRoute
        || body.provider_route
        || defaults.providerRoute
        || null;
    const effectiveProvider = error?.qmProvider
        || body.provider
        || providerRoute?.effectiveProvider
        || provider;
    return {
        provider: effectiveProvider || null,
        providerRoute: providerRoute || (effectiveProvider ? {
            requestedProvider: effectiveProvider,
            effectiveProvider,
            source,
            fallbackApplied: false,
        } : null),
        providerFallback: error?.qmProviderFallback
            || body.providerFallback
            || body.provider_fallback
            || defaults.providerFallback
            || null,
    };
}

function _annotateProviderError(error, defaults = {}) {
    if (!error || typeof error !== 'object') return error;
    const audit = _providerAuditFromError(error, defaults);
    try {
        error.qmProvider = audit.provider;
        error.qmProviderRoute = audit.providerRoute;
        error.qmProviderFallback = audit.providerFallback;
    } catch { /* frozen error: the original failure still propagates */ }
    return error;
}

const GATEWAY_ERROR_METADATA_FIELDS = Object.freeze([
    'code',
    'category',
    'feature',
    'model',
    'operatorAction',
    'retryable',
    'retryAfterSeconds',
    'policyBlocked',
    'upstreamStatusCode',
]);

function _hydrateGatewayError(error, body, { attachBody = true } = {}) {
    if (!error || typeof error !== 'object' || !body || typeof body !== 'object') return error;
    for (const field of GATEWAY_ERROR_METADATA_FIELDS) {
        if (body[field] !== undefined && body[field] !== null) {
            try { error[field] = body[field]; } catch { /* frozen error */ }
        }
    }
    if (attachBody) {
        try { error.body = body; } catch { /* frozen error */ }
    }
    return error;
}

function _providerAuditFromStreamResponse(response) {
    const provider = response?.headers?.get?.('x-qm-provider') || 'openrouter';
    const providerRoute = _decodeProviderAuditHeader(response?.headers?.get?.('x-qm-provider-route')) || {
        requestedProvider: provider,
        effectiveProvider: provider,
        source: 'backend-gateway-stream',
        fallbackApplied: false,
    };
    const providerFallback = _decodeProviderAuditHeader(response?.headers?.get?.('x-qm-provider-fallback'));
    return { provider, providerRoute, providerFallback };
}

function _assertEvalBackendSingleAttemptProof(evalAttempt, proof, route) {
    if (!evalAttempt) return;
    const valid = proof?.enforced === true
        && proof?.maxUpstreamAttempts === 1
        && Number.isInteger(proof?.upstreamAttempts)
        // Successful paid responses must prove exactly one dispatch. Zero is
        // reserved for failed/route-unavailable backend audit records.
        && proof.upstreamAttempts === 1;
    if (valid) return;
    const error = new Error(`Quote evaluation could not prove single-attempt enforcement at ${route}; the result was rejected.`);
    error.name = 'EvalBackendSingleAttemptProofError';
    error.code = 'QM_EVAL_BACKEND_SINGLE_ATTEMPT_UNPROVEN';
    error.route = route;
    throw error;
}

function _assertEvalBackendSingleAttemptStreamProof(evalAttempt, response, route) {
    if (!evalAttempt) return;
    const proof = _decodeProviderAuditHeader(
        response?.headers?.get?.('x-qm-evaluation-single-attempt'),
    );
    _assertEvalBackendSingleAttemptProof(evalAttempt, proof, route);
}

function buildProviderFailureMonitorMeta(error, model, extra = {}, defaults = {}) {
    const audit = _providerAuditFromError(error, defaults);
    const body = error?.body && typeof error.body === 'object' ? error.body : {};
    const usage = error?.usage && typeof error.usage === 'object' ? error.usage : {};
    const retryAfterValue = error?.retryAfterSeconds ?? body.retryAfterSeconds;
    const upstreamStatusValue = error?.upstreamStatusCode ?? body.upstreamStatusCode;
    return {
        model,
        ...audit,
        errorCode: error?.code || body.code || null,
        errorCategory: error?.category || body.category || null,
        operatorAction: error?.operatorAction || body.operatorAction || null,
        retryable: typeof error?.retryable === 'boolean'
            ? error.retryable
            : (typeof body.retryable === 'boolean' ? body.retryable : null),
        retryAfterSeconds: retryAfterValue !== undefined
            && retryAfterValue !== null
            && Number.isFinite(Number(retryAfterValue))
            ? Number(retryAfterValue)
            : null,
        upstreamStatusCode: upstreamStatusValue !== undefined
            && upstreamStatusValue !== null
            && Number.isFinite(Number(upstreamStatusValue))
            ? Number(upstreamStatusValue)
            : null,
        finishReason: error?.finishReason || null,
        truncated: error?.finishReason === 'length',
        tokensIn: usage.tokensIn,
        tokensOut: usage.tokensOut,
        cacheCreationTokens: usage.cacheCreationTokens,
        cacheReadTokens: usage.cacheReadTokens,
        reasoningTokens: usage.reasoningTokens,
        costUsd: usage.costUsd,
        ...extra,
    };
}

const _availabilityWarningCache = new Set();

function applyModelAvailabilityLane(task, pipelineId, model) {
    const route = resolveUnavailableModelLane(task || pipelineId || 'api-call', model);
    if (route.availabilityRerouted) {
        const cacheKey = `${task || pipelineId || 'api-call'}|${route.requestedModel}|${route.effectiveModel}`;
        if (!_availabilityWarningCache.has(cacheKey)) {
            _availabilityWarningCache.add(cacheKey);
            console.warn(`[AI-API] ${route.availabilityWarning}`);
        }
    }
    return route;
}

// v5.11.31 (2026-04-22): message.content can be a string OR an array of
// multimodal blocks, including text, image_url and native input_audio.
// Previously the monitor code did `content?.substring(0, 200)` which throws
// "s?.[0]?.content?.substring is not a function" on array-shaped content.
// Surface the text portion for logs/preview without blowing up on images.
// Peter 2026-04-22: "Pre-emptive photo auto-assign failed" was this bug path.
// the photo-auto-assign pipeline sends vision content blocks.
function previewContent(content, maxLen = 200) {
    if (typeof content === 'string') return content.substring(0, maxLen);
    if (Array.isArray(content)) {
        const text = content
            .filter(b => b && b.type === 'text' && typeof b.text === 'string')
            .map(b => b.text)
            .join(' ');
        if (text) return text.substring(0, maxLen);
        // All-media content: log the shape instead of the raw image/audio payload.
        return `(${content.length} content block${content.length === 1 ? '' : 's'})`;
    }
    return '';
}

function hasUsableAiContent(value) {
    if (typeof value === 'string') return value.trim().length > 0;
    return value != null;
}

function describeInvalidAiResponse(data, { model, pipelineId, stageName } = {}) {
    const finishReason = data?.finishReason || 'none';
    const toolCalls = Array.isArray(data?.toolCalls) ? data.toolCalls.length : 0;
    const tokensIn = data?.usage?.tokensIn ?? data?.usage?.prompt_tokens ?? null;
    const tokensOut = data?.usage?.tokensOut ?? data?.usage?.completion_tokens ?? null;
    const context = [pipelineId, stageName].filter(Boolean).join('/') || 'unknown';
    return `Invalid response from API: empty assistant content (model=${model || 'unknown'}, pipeline=${context}, finishReason=${finishReason}, toolCalls=${toolCalls}, tokensIn=${tokensIn ?? 'n/a'}, tokensOut=${tokensOut ?? 'n/a'})`;
}

// Estimate input-token length for a message whose content may be a string OR
// an array of multimodal content blocks. Falls back to 0 when nothing
// text-sized is available so the division-by-4 heuristic doesn't explode.
function contentLengthForTokens(content) {
    if (typeof content === 'string') return content.length;
    if (Array.isArray(content)) {
        return content
            .filter(b => b && b.type === 'text' && typeof b.text === 'string')
            .reduce((sum, b) => sum + b.text.length, 0);
    }
    return 0;
}

// ============================================================================
// GLOBAL CONCURRENCY LIMITER + 429 RETRY
// ----------------------------------------------------------------------------
// Today's parallel pipeline (Phase 2+3+WMS, Strategy+context prep, multi-doc)
// fires 10+ OpenRouter calls within seconds. OpenRouter per-account rate
// limits fire and half the calls fall back to degraded paths. Cap in-flight
// calls to 5 and retry 429s with exponential backoff (2s, 4s) before giving
// up. Both behaviours have kill switches for emergency rollback.
// ============================================================================

const DEFAULT_MAX_CONCURRENT_OPENROUTER = 5;

function _concurrencyCapDisabled() {
    try {
        if (typeof window !== 'undefined' && window.localStorage) {
            return !!localStorage.getItem('qm_disable_concurrency_cap');
        }
    } catch { /* unavailable */ }
    return false;
}

function _rateLimitRetryDisabled() {
    try {
        if (typeof window !== 'undefined' && window.localStorage) {
            return !!localStorage.getItem('qm_disable_rate_limit_retry');
        }
    } catch { /* unavailable */ }
    return false;
}

function _maxConcurrentOpenRouter() {
    try {
        if (typeof window !== 'undefined' && window.localStorage) {
            const raw = parseInt(localStorage.getItem('qm_max_concurrent_openrouter') || '', 10);
            if (Number.isFinite(raw) && raw > 0 && raw <= 50) return raw; // why: sanity cap
        }
    } catch { /* unavailable */ }
    return DEFAULT_MAX_CONCURRENT_OPENROUTER;
}

let _activeCount = 0;
const _waitQueue = [];

function acquireSlot() {
    if (_concurrencyCapDisabled()) return Promise.resolve();
    return new Promise(resolve => {
        if (_activeCount < _maxConcurrentOpenRouter()) {
            _activeCount++;
            resolve();
        } else {
            _waitQueue.push(resolve);
        }
    });
}

function releaseSlot() {
    if (_concurrencyCapDisabled()) return;
    const next = _waitQueue.shift();
    if (next) {
        next(); // why: transfer the active slot directly to the waiter - no dip to zero
    } else {
        _activeCount = Math.max(0, _activeCount - 1);
    }
}

// Default per-request timeout. A hung connection used to block the whole
// import pipeline indefinitely (Customer Zero 2026-04-21: import stalled at
// 15/28 after a Gemini transcription's fetch hung: the semaphore slot was
// held, blocking every downstream AI call). 120s is generous for large
// vision + transcription payloads while still failing fast enough that one
// dead connection can't stall the pipeline.
const FETCH_TIMEOUT_MS = 120_000;
// Backend non-streaming calls have their own provider deadline. Keep the
// browser transport open long enough for the backend to abort the provider,
// write truthful usage provenance, and return its typed 504. An explicit
// timeoutMs:0 retains the established "no client timer" long-pipeline mode,
// while the server still receives a bounded ten-minute deadline.
const GATEWAY_LONG_PROVIDER_TIMEOUT_MS = 600_000;
const GATEWAY_TRANSPORT_TIMEOUT_CUSHION_MS = 10_000;

function _resolveGatewayTimeouts(timeoutMs) {
    const requested = Number(timeoutMs);
    const hasPositiveExplicitDeadline = Number.isFinite(requested) && requested > 0;
    const explicitlyDisablesClientTimer = Number.isFinite(requested)
        && timeoutMs !== undefined
        && timeoutMs !== null
        && requested <= 0;
    const providerTimeoutMs = hasPositiveExplicitDeadline
        ? Math.max(1, Math.floor(requested))
        : explicitlyDisablesClientTimer
            ? GATEWAY_LONG_PROVIDER_TIMEOUT_MS
            : FETCH_TIMEOUT_MS;
    return {
        providerTimeoutMs,
        clientTransportTimeoutMs: explicitlyDisablesClientTimer
            ? 0
            : providerTimeoutMs + GATEWAY_TRANSPORT_TIMEOUT_CUSHION_MS,
    };
}

function _fetchTimeoutDisabled() {
    try {
        if (typeof window !== 'undefined' && window.localStorage) {
            return !!localStorage.getItem('qm_disable_fetch_timeout');
        }
    } catch { /* unavailable */ }
    return false;
}

// Compose an AbortSignal that fires when either the caller's signal aborts OR
// a timeout fires. Uses AbortSignal.any when available (Chrome 116+, Safari 17.4+)
// with a manual fallback for older engines. Returns an object with the composed
// signal plus a cleanup fn that clears the timeout to avoid leaks.
function _makeRequestSignal(callerSignal, timeoutMs) {
    if (_fetchTimeoutDisabled() || !timeoutMs || timeoutMs <= 0) {
        return {
            signal: callerSignal,
            cleanup: () => {},
            hasClientTimeout: false,
            didClientTimeout: () => false,
        };
    }
    // AbortSignal.timeout throws a "TimeoutError" (a DOMException). That's what
    // we want downstream code to see rather than a generic AbortError, so a
    // genuine user cancel ("AbortError") remains distinguishable from a timeout.
    const timeoutSignal = AbortSignal.timeout(timeoutMs);
    if (!callerSignal) {
        return {
            signal: timeoutSignal,
            cleanup: () => {},
            hasClientTimeout: true,
            didClientTimeout: () => timeoutSignal.aborted,
        };
    }
    if (typeof AbortSignal.any === 'function') {
        const composedSignal = AbortSignal.any([callerSignal, timeoutSignal]);
        return {
            signal: composedSignal,
            cleanup: () => {},
            hasClientTimeout: true,
            // Compare the winning reason by identity. A backend/upstream
            // TimeoutError can reject the fetch while this timer is armed;
            // merely having a configured timer does not make that error local.
            didClientTimeout: () => timeoutSignal.aborted
                && composedSignal.aborted
                && composedSignal.reason === timeoutSignal.reason,
        };
    }
    // Fallback for older engines. Manually forward either abort to a fresh controller.
    const ctl = new AbortController();
    const onCallerAbort = () => ctl.abort(callerSignal.reason);
    const onTimeout = () => ctl.abort(
        timeoutSignal.reason || new DOMException('The operation timed out.', 'TimeoutError'),
    );
    if (callerSignal.aborted) { onCallerAbort(); }
    else { callerSignal.addEventListener('abort', onCallerAbort, { once: true }); }
    if (timeoutSignal.aborted) { onTimeout(); }
    else { timeoutSignal.addEventListener('abort', onTimeout, { once: true }); }
    const cleanup = () => {
        try { callerSignal.removeEventListener('abort', onCallerAbort); } catch { /* noop */ }
        try { timeoutSignal.removeEventListener('abort', onTimeout); } catch { /* noop */ }
    };
    return {
        signal: ctl.signal,
        cleanup,
        hasClientTimeout: true,
        didClientTimeout: () => timeoutSignal.aborted
            && ctl.signal.aborted
            && ctl.signal.reason === timeoutSignal.reason,
    };
}

function _markClientTimeoutSource(error, didClientTimeout) {
    if (!error || typeof error !== 'object' || didClientTimeout?.() !== true) return;
    try { error.qmTimeoutSource = 'client-fetch'; } catch { /* frozen */ }
}

// Classify an aborted-fetch error so Pipeline Monitor can tell a genuine user
// cancel apart from a client-side fetch timeout. Prior to 2026-04-25 every
// AbortError was tagged "Cancelled by user", which hid timeout-induced aborts
// (e.g. WMS at the 120s FETCH_TIMEOUT_MS default) behind a user-cancel label.
// TimeoutError is emitted by AbortSignal.timeout(); AbortError is emitted by
// an explicit ctrl.abort() from caller code. Returns { label, cancelled } where
// `cancelled` drives the monitor's "cancelled" status vs "error" status.
function _abortReasonText(err) {
    const parts = [
        typeof err === 'string' ? err : '',
        err?.message,
        typeof err?.reason === 'string' ? err.reason : '',
        err?.reason?.message,
        err?.cause?.message,
    ];
    return parts.filter(Boolean).join(' ');
}

function classifyAbortError(err) {
    const name = err?.name;
    const reasonName = err?.reason?.name;
    const reasonText = _abortReasonText(err);
    if (/inspector-notes/i.test(reasonText)) {
        return { label: 'Paused for inspector notes edit', cancelled: true };
    }
    // A TimeoutError that did NOT originate from a configured client fetch
    // timeout (D10: long-pipeline calls pass timeoutMs:0) is necessarily an
    // upstream/transport blip surfaced through the backend gateway. Label it
    // truthfully so users (and Pipeline Monitor readers) don't mis-attribute
    // long-pipeline failures to a phantom client-side timeout. The literal
    // "client fetch timeout" label is reserved for the case where the caller
    // explicitly composed an AbortSignal.timeout against the request.
    if (name === 'TimeoutError' || reasonName === 'TimeoutError') {
        const composedHere = err?.qmTimeoutSource === 'client-fetch';
        return {
            label: composedHere
                ? 'Timed out (client fetch timeout)'
                : 'Upstream/network timeout',
            cancelled: false,
        };
    }
    if (name === 'AbortError' || reasonName === 'AbortError') {
        return { label: 'Cancelled by user', cancelled: true };
    }
    if (/^(aborted|abort|cancelled|canceled)$/i.test(reasonText.trim())) {
        return { label: 'Cancelled by user', cancelled: true };
    }
    return null;
}

// Exec fetch with 429 retry + global concurrency slot + per-request timeout.
// Centralised so every call site (chat, tools, vision, streaming, embeddings)
// gets the same guardrails. AbortError is never counted as a rate-limit retry -
// it propagates immediately so user cancel stays snappy.
async function fetchWithRateLimitRetry(url, init, {
    label = 'openrouter',
    timeoutMs = FETCH_TIMEOUT_MS,
    evalAttempt = null,
    evalTask = null,
    evalModel = null,
} = {}) {
    await acquireSlot();
    const {
        signal: reqSignal,
        cleanup: cleanupSignal,
        didClientTimeout,
    } = _makeRequestSignal(init?.signal, timeoutMs);
    const requestInit = reqSignal ? { ...init, signal: reqSignal } : init;
    try {
        const configuredMaxRetries = _rateLimitRetryDisabled() ? 0 : 2;
        const maxRetries = isEvalAiSingleAttemptPolicyActive() ? 0 : configuredMaxRetries;
        for (let attempt = 0; attempt <= maxRetries; attempt++) {
            let response;
            try {
                response = await fetch(url, requestInit);
            } catch (err) {
                _markClientTimeoutSource(err, didClientTimeout);
                if (classifyAbortError(err)) throw err; // why: user cancel and timeout both skip retry
                throw err;
            }
            if (response.status !== 429 || attempt === maxRetries) {
                if (
                    response.status === 429
                    && configuredMaxRetries > maxRetries
                ) {
                    suppressEvalAiRetry('rate_limit_retry', {
                        handle: evalAttempt,
                        route: label,
                        task: evalTask,
                        model: evalModel,
                        reason: 'provider_returned_429',
                    });
                }
                return response;
            }
            const waitMs = 2000 * Math.pow(2, attempt); // 2s then 4s
            console.warn(`[AI-API] Rate-limited, retrying in ${waitMs / 1000}s (attempt ${attempt + 1}/${maxRetries + 1})`);
            await new Promise((resolve, reject) => {
                const t = setTimeout(resolve, waitMs);
                if (requestInit?.signal) {
                    if (requestInit.signal.aborted) { clearTimeout(t); reject(Object.assign(new Error('Aborted'), { name: requestInit.signal.reason?.name === 'TimeoutError' ? 'TimeoutError' : 'AbortError' })); return; }
                    requestInit.signal.addEventListener('abort', () => { clearTimeout(t); reject(Object.assign(new Error('Aborted'), { name: requestInit.signal.reason?.name === 'TimeoutError' ? 'TimeoutError' : 'AbortError' })); }, { once: true });
                }
            });
        }
        // Unreachable - loop always returns or throws
        throw new Error(`fetchWithRateLimitRetry[${label}]: retry loop exited unexpectedly`);
    } finally {
        cleanupSignal();
        releaseSlot();
    }
}

// Retry wrapper with exponential backoff - critical for enterprise reliability
export async function withRetry(fn, {
    maxRetries = 2,
    baseDelay = 2000,
    retryableErrors = [429, 500, 502, 503, 504],
    evalAttempt = null,
    evalTask = null,
    evalModel = null,
} = {}) {
    const configuredMaxRetries = maxRetries;
    const effectiveMaxRetries = isEvalAiSingleAttemptPolicyActive() ? 0 : maxRetries;
    for (let attempt = 0; attempt <= effectiveMaxRetries; attempt++) {
        try {
            return await fn();
        } catch (err) {
            const isRetryable = retryableErrors.some(code => err.message?.includes(String(code))) ||
                err.message?.includes('Rate limit') ||
                err.message?.includes('timeout') ||
                err.message?.includes('ECONNRESET') ||
                err.message?.includes('fetch failed');

            if (attempt === effectiveMaxRetries || !isRetryable) {
                if (isRetryable && configuredMaxRetries > effectiveMaxRetries) {
                    suppressEvalAiRetry('transient_retry', {
                        handle: evalAttempt,
                        task: evalTask,
                        model: evalModel,
                        reason: err?.message || 'retryable_provider_error',
                    });
                }
                throw err;
            }

            const delay = baseDelay * Math.pow(2, attempt) + Math.random() * 500;
            console.warn(`[Retry] Attempt ${attempt + 1}/${effectiveMaxRetries} after ${Math.round(delay)}ms - ${err.message}`);
            await new Promise(r => setTimeout(r, delay));
        }
    }
}

// Max output tokens for a model from cached capabilities. Output-token billing
// means unused tokens cost nothing - the ceiling is headroom, not a target.
// Silent truncation via a low cap is an anti-pattern on the long structured
// authoring routes; intentional concision belongs in their prompts/schemas.
// Small classifiers and extractors keep their explicit bounded maxTokens at
// their own call sites and do not use getMaxOutputTokensForTask().
const DEFAULT_MAX_OUTPUT_TOKENS = 16384;

// Built-in per-model OUTPUT-token ceilings, verified against OpenRouter's live
// /api/v1/models catalogue (2026-07-01). This is the CODE-LEVEL floor so a known
// model is never capped at the tiny DEFAULT (16384) just because the localStorage
// `qm_model_capabilities` map was never seeded — it is written ONLY when
// SettingsModal mounts + the OpenRouter fetch runs, so a fresh install or a
// browser that never opened Settings would otherwise run every quote-critical
// task (quote-generation/risk/WMS/strategy all call getMaxOutputTokens) at 16k
// regardless of the model's real ceiling (Opus 4.8 = 128k). Exact slugs first,
// then a conservative family fallback so model-version bumps keep a sane ceiling
// without a code edit.
const MODEL_OUTPUT_CEILINGS = Object.freeze({
    'anthropic/claude-opus-4.8': 128000,
    'anthropic/claude-opus-4.7': 128000,
    'anthropic/claude-opus-4.6': 128000,
    // Sonnet 5 lifted the Sonnet-line ceiling to Opus parity. Live OpenRouter
    // probe 2026-07-11: max_completion_tokens = 128000 on every endpoint.
    // (The `claude-sonnet` family fallback below stays 64000 for unlisted
    // versions — conservative on purpose.)
    'anthropic/claude-sonnet-5': 128000,
    'anthropic/claude-sonnet-4.6': 64000,
    'anthropic/claude-haiku-4.5': 64000,
    'google/gemini-3.1-pro-preview': 65536,
    'google/gemini-3.1-flash-lite': 65536,
    'google/gemini-3.1-flash-lite-preview': 65536,
    'google/gemini-3-flash-preview': 65536,
    'google/gemini-3-pro-preview': 65536,
    'z-ai/glm-5.2': 131072,
    'z-ai/glm-5.3': 131072,
    'z-ai/glm-5v-turbo': 131072,
    // v5.53.0 routing refresh. WITHOUT these the OpenAI and xAI lanes resolved
    // null here and silently fell back to DEFAULT_MAX_OUTPUT_TOKENS (16384),
    // which would have cut quote-generation's output budget from the 128000 it
    // had on Opus 4.8 down to 12.8% of that. Values are the advertised
    // max_completion_tokens from the live OpenRouter catalogue (2026-08-07).
    'openai/gpt-5.6-sol': 128000,
    'openai/gpt-5.6-terra': 128000,
    'openai/gpt-5.6-luna': 128000,
    'openai/gpt-5.4-nano': 128000,
    'openai/gpt-5.4-mini': 128000,
    // xAI does NOT advertise max_completion_tokens on OpenRouter, so this is a
    // deliberate conservative floor well under the 500k context, not a probed
    // value. Raise it only against a real endpoint probe.
    'x-ai/grok-4.5': 32768,
    'x-ai/grok-4.6': 32768,
    'google/gemini-3.5-flash-lite': 65536,
    'google/gemini-3.6-flash': 65536,
    'google/gemini-3.7-flash': 65536,
    'google/gemini-3.8-flash': 65536,
    // Audio lane. PRE-EXISTING gap, not introduced by the routing refresh:
    // voxtral has never had an entry here, so transcription has always run at
    // the 16384 default. Neither slug advertises max_completion_tokens, so
    // these are bounded by the 32k context rather than probed. Real
    // transcripts in the production log run 106-138 output tokens, so the
    // headroom is ample either way.
    'mistralai/voxtral-small-24b-2507': 16384,
    'mistralai/voxtral-mini-tts-2603': 4096,
    // Reachable only via the opt-in experiment gears (Budget Reasoning Lab,
    // Video Challenger Lab). Listed so applying a gear cannot silently drop an
    // operator onto the truncating default.
    'moonshotai/kimi-k2.6': 262144,
    'moonshotai/kimi-k3': 128000,
    'qwen/qwen3.5-plus-20260420': 65536,
    // 2026-09-29 registrations, from the live OpenRouter catalogue's
    // max_completion_tokens. Grok 4.7 keeps the xAI 32768 floor (the catalogue
    // lists 450000; the Assistant measurement ran at 32768) and GLM 5.3 Flash
    // keeps the GLM 131072 floor used for glm-5.3.
    //
    // Claude Fable 5.1 and GPT-6 Astra are held to 64000 (the catalogue lists
    // 128000). At $50 per MTok of output, 128000 prices at $6.40 before the
    // prompt, and the gateway prices the whole ceiling against the per-call
    // cap, so every call was refused where the cap is the $5.00 default (a
    // tenant with no AI policy row, every demo business). 64000 prices at
    // $3.20, the same output spend as Opus 4.8 at its 128000.
    'deepseek/deepseek-v4.1-flash': 384000,
    'qwen/qwen3.8-max-0902': 131072,
    'x-ai/grok-4.7': 32768,
    'anthropic/claude-opus-5.5': 128000,
    'anthropic/claude-fable-5.1': 64000,
    'openai/gpt-6-sol': 128000,
    'openai/gpt-6-luna': 128000,
    'openai/gpt-6-astra': 64000,
    'z-ai/glm-5.3-flash': 131072,
    // 2026-10-08 registrations (SPEC 2026-10-08 D13), the catalogue's
    // max_completion_tokens. Sonnet and Haiku 5.5 list 128000, above the
    // 64000 their family fallbacks below assume. Gemini 3.1 Pro preview is
    // listed above.
    'anthropic/claude-sonnet-5.5': 128000,
    'anthropic/claude-haiku-5.5': 128000,
    'openai/gpt-6.1-sol': 128000,
});

/**
 * Resolve a model's real output-token ceiling from the built-in map, with a
 * conservative family fallback. Returns null for genuinely unknown models so
 * callers fall back to DEFAULT_MAX_OUTPUT_TOKENS.
 */
export function resolveModelOutputCeiling(modelId) {
    if (!modelId || typeof modelId !== 'string') return null;
    const id = modelId.toLowerCase();
    if (MODEL_OUTPUT_CEILINGS[id]) return MODEL_OUTPUT_CEILINGS[id];
    if (id.includes('claude-opus')) return 128000;
    if (id.includes('claude-sonnet')) return 64000;
    if (id.includes('claude-haiku')) return 64000;
    if (id.startsWith('google/gemini-3')) return 65536;
    if (/^z-ai\/glm-5/.test(id)) return 131072;
    // v5.53.0: family fallbacks for the providers the routing refresh moved
    // onto, so a model-version bump (terra -> terra-next) keeps a sane ceiling
    // instead of silently collapsing to the 16384 default. Conservative
    // relative to the advertised 128000 on the exact slugs above.
    if (/^openai\/gpt-5/.test(id)) return 128000;
    if (/^openai\/gpt-6/.test(id)) return 128000;
    if (id.startsWith('x-ai/grok')) return 32768;
    return null;
}

// Kill switch: localStorage.qm_disable_model_ceiling_map = 'true' reverts
// getMaxOutputTokens to the pre-v5.46.45 behaviour (live override or the 16384
// default, no built-in ceiling floor) for safety rollback.
function _modelCeilingMapDisabled() {
    try {
        if (typeof localStorage !== 'undefined') {
            return localStorage.getItem('qm_disable_model_ceiling_map') === 'true';
        }
    } catch (_e) { /* localStorage unavailable */ }
    return false;
}

// Kill switch: localStorage.qm_disable_truncation_retry = 'true' reverts to the
// pre-v5.46.45 behaviour (truncated JSON-mode output is returned as-is for the
// caller to parse, no retry-at-ceiling and no AI_TRUNCATED throw).
function _truncationRetryDisabled() {
    try {
        if (typeof localStorage !== 'undefined') {
            return localStorage.getItem('qm_disable_truncation_retry') === 'true';
        }
    } catch (_e) { /* localStorage unavailable */ }
    return false;
}

/**
 * Effective max output tokens for a model. Prefers the live SettingsModal /
 * OpenRouter override when present, but never lets it sit BELOW the verified
 * built-in ceiling (so a stale curated under-value — e.g. the old Haiku 16384 —
 * cannot cap a model below its real max). Falls back to the built-in ceiling,
 * then the unknown-model default. This is what makes large quotes non-limiting.
 */
export function getMaxOutputTokens(modelId) {
    let override = null;
    try {
        const caps = JSON.parse(localStorage.getItem('qm_model_capabilities') || '{}');
        const m = caps[modelId]?.maxOutputTokens;
        if (Number.isFinite(m) && m > 0) override = m;
    } catch { /* localStorage unavailable */ }
    if (_modelCeilingMapDisabled()) return override || DEFAULT_MAX_OUTPUT_TOKENS;
    const builtin = resolveModelOutputCeiling(modelId);
    if (override && builtin) return Math.max(override, builtin);
    return override || builtin || DEFAULT_MAX_OUTPUT_TOKENS;
}

/**
 * Compatibility entry point for long structured task callers. These routes
 * use the selected model's full verified output ceiling so they cannot be cut
 * off by QuoteMaker below what the provider supports. `taskName` remains in
 * the signature for telemetry/readability and so existing callers do not need
 * a second migration.
 */
export function getMaxOutputTokensForTask(_taskName, modelId) {
    return getMaxOutputTokens(modelId);
}

// ============================================================================
// MULTIMODAL BODY BUILDER (OpenAI shape - images/PDFs + native audio blocks)
// ============================================================================
const AUDIO_FORMAT_BY_MIME = Object.freeze({
    'wav': 'wav',
    'x-wav': 'wav',
    'wave': 'wav',
    'mpeg': 'mp3',
    'mp3': 'mp3',
    'mp4': 'm4a',
    'x-m4a': 'm4a',
    'm4a': 'm4a',
    'aac': 'aac',
    'ogg': 'ogg',
    'flac': 'flac',
    'webm': 'webm',
});

export function buildOpenRouterMediaContent(prompt, media = []) {
    const content = [{ type: 'text', text: prompt }];
    media.forEach(img => {
        const dataUrl = (typeof img === 'string') ? img : img?.data;
        if (!dataUrl || typeof dataUrl !== 'string') return;
        if (dataUrl.startsWith('data:image') || dataUrl.startsWith('data:application/pdf')) {
            content.push({ type: 'image_url', image_url: { url: dataUrl, detail: 'auto' } });
            return;
        }
        if (dataUrl.startsWith('data:audio')) {
            const match = /^data:audio\/([^;,]+)(?:;[^,]*)?;base64,([A-Za-z0-9+/=\r\n]+)$/i.exec(dataUrl);
            if (!match) return;
            const mimeSubtype = String(match[1] || '').toLowerCase();
            const format = AUDIO_FORMAT_BY_MIME[mimeSubtype] || mimeSubtype.replace(/^x-/, '');
            content.push({
                type: 'input_audio',
                input_audio: {
                    data: match[2].replace(/\s+/g, ''),
                    format,
                },
            });
        }
    });
    if (content.length === 1) throw new Error('No valid media found (expected image, PDF, or audio data URL)');
    return content;
}

function isNativeAudioOnlyTranscriptionRequest(task, media = []) {
    if (task !== 'audio-transcription' && task !== 'video-processing') return false;
    if (!Array.isArray(media) || media.length !== 1) return false;
    const item = media[0];
    const dataUrl = typeof item === 'string' ? item : item?.data;
    return typeof dataUrl === 'string'
        && /^data:audio\/[^;,]+(?:;[^,]*)?;base64,[A-Za-z0-9+/=\r\n]+$/i.test(dataUrl);
}

function buildVisionBody({ model, prompt, images, temperature, maxTokens, responseFormat, reasoningEffort }) {
    const content = buildOpenRouterMediaContent(prompt, images);
    const body = {
        model: OPENROUTER_PROVIDER.transformModel(model),
        messages: [{ role: 'user', content }],
        temperature,
    };
    if (Number.isFinite(maxTokens) && maxTokens > 0) body.max_tokens = maxTokens;
    if (responseFormat) body.response_format = responseFormat;
    // Browser-direct is a local-development escape hatch, but it must preserve
    // the same explicit vision semantics as the production gateway path.
    if (reasoningEffort) body.reasoning = { effort: reasoningEffort };
    return body;
}

// ============================================================================
// CORE OPENROUTER CALL - multi-key rotation with rate-limit handling
// ============================================================================
// Picks the next available OpenRouter key via round-robin. If a 429 comes
// back, marks that key as rate-limited (60s cooldown) and tries the next
// available key. If all keys are exhausted, returns the original error.
async function callWithFailover({
    apiKey,            // legacy single-key (used only as last-resort fallback)
    model,
    messages,          // text mode
    temperature,
    maxTokens,
    isVision = false,  // when true, use prompt+images instead of messages
    prompt,
    images,
    signal,            // optional AbortSignal for cancellable requests
    tools,             // optional OpenAI-compatible tool definitions
    tool_choice,       // optional tool_choice constraint ('auto'|'none'|{type,function})
    responseFormat,    // optional { type: 'json_object' } or json_schema for forced structured output
    task,              // Phase A log-only: task identifier so model-config.js can resolve per-task defaults
    timeoutMs,         // optional per-call fetch timeout override (long assistant agent turns pass 600000)
    reasoningEffort,   // optional explicit OpenRouter reasoning tier for a verified vision route
    evalAttempt,       // evaluator-only logical-stage token (null in normal QuoteMaker use)
}) {
    validateModel(model);
    assertBrowserOpenRouterAllowed();

    // Browser-direct is an explicit local-development escape hatch. Provider
    // selection and Bedrock -> OpenRouter recovery belong to the authenticated
    // backend gateway; an unauthenticated browser fetch to /api/ai/chat can
    // never be a valid production failover path.
    const providerRouteSource = getAiGatewayMode() === 'hybrid'
        ? 'hybrid-browser-direct'
        : 'browser-direct';
    const currentProviderRoute = () => ({
        requestedProvider: 'openrouter',
        effectiveProvider: 'openrouter',
        source: providerRouteSource,
        fallbackApplied: false,
    });
    const annotateProviderFailure = (error) => {
        return _annotateProviderError(error, {
            provider: 'openrouter',
            source: providerRouteSource,
        });
    };

    // Build the request body once - same body whether we're rotating keys or not
    const body = isVision
        ? buildVisionBody({ model, prompt, images, temperature, maxTokens, responseFormat, reasoningEffort })
        : OPENROUTER_PROVIDER.buildBody({ model, messages, temperature, maxTokens, responseFormat, tools, tool_choice, task });

    // Determine how many key rotation attempts to make (one per configured key,
    // up to a maximum of 5 to avoid runaway loops)
    const allKeys = getOpenRouterKeys();
    const configuredMaxAttempts = Math.max(1, Math.min(5, allKeys.length || 1));
    const maxAttempts = evalAiAttemptLimit(configuredMaxAttempts);

    let lastError = null;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
        // Pick the next available key (round-robin, skipping cooldowns)
        const key = getNextOpenRouterKey() || apiKey;
        if (!key) {
            throw annotateProviderFailure(new Error('No OpenRouter API key configured. Add one in AI Models page.'));
        }

        try {
            // Wrap each individual key attempt in withRetry for transient errors
            // (5xx, fetch failed, timeout). We do NOT retry 429 here because
            // that's handled by switching keys.
            const result = await withRetry(async () => {
                const serializedBody = JSON.stringify(body);
                const response = await withEvalAiPaidRequest(evalAttempt, {
                    route: isVision ? 'browser-direct-vision' : 'browser-direct-chat',
                    task,
                    model,
                    requestPayload: serializedBody,
                }, () => fetchWithRateLimitRetry(OPENROUTER_PROVIDER.endpoint, {
                    method: 'POST',
                    headers: OPENROUTER_PROVIDER.headers(key),
                    body: serializedBody,
                    signal, // pass the AbortSignal through to fetch so cancel() aborts mid-flight
                }, {
                    label: 'chat',
                    ...(timeoutMs !== undefined ? { timeoutMs } : {}),
                    evalAttempt,
                    evalTask: task,
                    evalModel: model,
                }));

                if (response.status === 429 || response.status === 402) {
                    // 429 = rate limit (60s cooldown), 402 = spend cap hit (1h cooldown).
                    // Either way, mark this key down and rotate to the next configured
                    // key so a single exhausted/burnt key doesn't halt the whole pipeline.
                    const cooldownMs = response.status === 402 ? 60 * 60 * 1000 : undefined;
                    markKeyRateLimited(key, cooldownMs);
                    const label = response.status === 402 ? 'spend cap (402)' : 'rate limit (429)';
                    const err = new Error(`OpenRouter ${label} - rotating key`);
                    err.isRateLimit = true;
                    throw err;
                }

                if (!response.ok) {
                    const errData = await response.json().catch(() => ({}));
                    const msg = errData.error?.message || errData.error || errData.message || 'OpenRouter error';
                    // Log the full error body + request summary so the next 400
                    // is debuggable. OpenRouter's rejection reasons (bad model,
                    // bad tool schema, oversized messages) are otherwise invisible.
                    try {
                        console.warn('[OpenRouter] Request failed', {
                            status: response.status,
                            error: errData,
                            requestModel: body?.model,
                            requestMessages: Array.isArray(body?.messages) ? body.messages.length : 0,
                            requestTools: Array.isArray(body?.tools) ? body.tools.length : 0,
                            hasResponseFormat: !!body?.response_format,
                        });
                    } catch { /* noop */ }
                    throw new Error(`${msg} (status ${response.status})`);
                }

                const data = await response.json();
                return {
                    content: OPENROUTER_PROVIDER.parseResponse(data),
                    usage: OPENROUTER_PROVIDER.parseUsage(data),
                    toolCalls: OPENROUTER_PROVIDER.parseToolCalls(data),
                    annotations: OPENROUTER_PROVIDER.parseAnnotations?.(data) || [],
                    finishReason: OPENROUTER_PROVIDER.parseFinishReason?.(data) || null,
                    provider: 'openrouter',
                    providerRoute: currentProviderRoute(),
                    providerFallback: null,
                };
            }, {
                maxRetries: 1,
                baseDelay: 1500,
                retryableErrors: [500, 502, 503, 504],
                evalAttempt,
                evalTask: task,
                evalModel: model,
            });

            // Success on this attempt - return immediately
            return result;
        } catch (err) {
            lastError = err;
            // Propagate aborts immediately - no key rotation, no retry.
            if (classifyAbortError(err)) {
                throw err;
            }
            // If this was a rate limit, try the next key in the next loop iteration
            if (err.isRateLimit && attempt < maxAttempts - 1) {
                console.warn(`[OpenRouter] Key ${attempt + 1}/${maxAttempts} rate-limited, rotating...`);
                continue;
            }
            if (err.isRateLimit && configuredMaxAttempts > maxAttempts) {
                suppressEvalAiRetry('key_rotation', {
                    handle: evalAttempt,
                    route: 'browser-direct-chat',
                    task,
                    model,
                    reason: err?.message || 'provider_key_unavailable',
                });
            }
            // If it's not a rate limit OR we've exhausted keys, throw
            throw annotateProviderFailure(err);
        }
    }

    // Should not reach here, but be defensive
    throw annotateProviderFailure(lastError || new Error('All OpenRouter keys failed'));
}

/**
 * Parse an SSE stream (OpenAI-compatible format) into accumulated content.
 * Backend gateway and explicit local OpenRouter mode both emit compatible
 * `data: {"choices":[{"delta":{"content":"..."}}]}` chunks.
 */
async function parseSSEStream(response, onChunk, signal, { trustedGatewayMetadata = false } = {}) {
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let fullContent = '';
    let buffer = '';
    let streamUsage = null;
    let streamFinishReason = null;
    let sawDone = false;
    let eventType = 'message';

    const terminalStreamError = (error = {}) => {
        const streamError = new Error(
            trustedGatewayMetadata && typeof error.message === 'string'
                ? error.message
                : 'AI stream interrupted before completion',
        );
        streamError.code = trustedGatewayMetadata && typeof error.code === 'string'
            ? error.code
            : 'upstream_stream_error';
        if (trustedGatewayMetadata) {
            _hydrateGatewayError(streamError, error, { attachBody: false });
        }
        if (streamError.code === 'upstream_timeout') streamError.name = 'TimeoutError';
        streamError.qmTerminalStreamError = true;
        return streamError;
    };

    const parseLine = (line) => {
        const trimmed = String(line || '').trim();
        if (!trimmed) {
            if (eventType === 'error') throw terminalStreamError();
            eventType = 'message';
            return;
        }
        if (trimmed.startsWith('event:')) {
            eventType = trimmed.slice(6).trim().toLowerCase() || 'message';
            return;
        }
        if (!trimmed.startsWith('data:')) return;

        const payload = trimmed.slice(5).trimStart();
        if (payload === '[DONE]') {
            sawDone = true;
            return;
        }

        let json;
        try {
            json = JSON.parse(payload);
        } catch {
            if (eventType === 'error') throw terminalStreamError();
            return;
        }
        if (eventType === 'error' || json?.error) {
            throw terminalStreamError(json?.error);
        }

        const delta = json.choices?.[0]?.delta?.content || '';
        if (delta) {
            fullContent += delta;
            if (onChunk) onChunk(fullContent, delta);
        }
        const finishReason = json.choices?.[0]?.finish_reason ?? json.choices?.[0]?.finishReason;
        if (typeof finishReason === 'string' && finishReason.trim()) {
            streamFinishReason = finishReason.trim();
        }
        if (json.usage) {
            // Workstream E2 (2026-04-24): carry cache + reasoning
            // + cost through the streaming path too. Shape mirrors
            // OPENROUTER_PROVIDER.parseUsage; fields are null/0
            // when the provider doesn't supply them.
            streamUsage = {
                tokensIn: json.usage.prompt_tokens || json.usage.inputTokens || 0,
                tokensOut: json.usage.completion_tokens || json.usage.outputTokens || 0,
                cacheCreationTokens: json.usage.cache_creation_input_tokens
                    || json.usage.prompt_tokens_details?.cache_creation_tokens
                    || 0,
                cacheReadTokens: json.usage.cache_read_input_tokens
                    || json.usage.prompt_tokens_details?.cached_tokens
                    || 0,
                reasoningTokens: json.usage.completion_tokens_details?.reasoning_tokens
                    || json.usage.reasoning_tokens
                    || 0,
                costUsd: (typeof json.usage.cost === 'number') ? json.usage.cost
                    : (typeof json.generation?.cost === 'number') ? json.generation.cost
                    : null,
            };
        }
    };

    try {
        while (true) {
            if (signal?.aborted) {
                if (signal.reason) throw signal.reason;
                const abortError = new Error('Aborted');
                abortError.name = 'AbortError';
                throw abortError;
            }
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() || '';
            for (const line of lines) {
                parseLine(line);
                if (sawDone) break;
            }
            if (sawDone) {
                try { await reader.cancel?.(); } catch { /* already closed */ }
                break;
            }
        }

        buffer += decoder.decode();
        if (buffer) {
            for (const line of buffer.split('\n')) {
                parseLine(line);
                if (sawDone) break;
            }
        }
        if (!sawDone) {
            throw terminalStreamError({
                code: 'stream_incomplete',
                message: 'AI stream ended before the completion marker',
            });
        }
    } catch (readErr) {
        if (signal?.aborted || readErr?.name === 'AbortError') throw readErr;
        // Partial model output is never a valid completed response. The old
        // salvage path could turn a clean EOF/network truncation into a quote
        // that looked successful, so every non-cancelled read failure rejects.
        throw readErr;
    }

    return {
        content: fullContent,
        usage: streamUsage || { tokensIn: 0, tokensOut: 0 },
        finishReason: streamFinishReason,
    };
}

function assertStreamingOutputComplete(result, pipelineId) {
    if (result?.finishReason !== 'length') return;
    const err = createTruncatedOutputError(pipelineId || 'streaming output', result?.usage);
    err.message = `AI output was truncated at the model token limit (finish_reason=length); ${pipelineId || 'streaming output'} is incomplete and will not be retried automatically.`;
    throw err;
}

function createTruncatedOutputError(pipelineId, usage = null) {
    const err = new Error(`AI output was truncated at the model token limit (finish_reason=length); ${pipelineId || 'AI output'} is incomplete.`);
    err.code = 'AI_TRUNCATED';
    err.category = 'ai-output-truncated';
    err.retryable = false;
    err.aiCallMade = true;
    err.finishReason = 'length';
    err.usage = usage && typeof usage === 'object'
        ? { ...usage }
        : { tokensIn: 0, tokensOut: 0 };
    return err;
}

// ============================================
// PUBLIC API - backward-compatible wrappers
// ============================================
// `callOpenRouter` keeps its name and signature so the dozens of existing
// call sites don't need any changes. Internally it now goes through the
// multi-provider failover loop.
export async function callOpenRouter({ apiKey, model, messages, temperature = 0.2, maxTokens = /** @type {number | null} */ (null), pipelineId, stageName, signal, responseFormat, task, timeoutMs, projectId, originator, modelRouteAttempt, logicalCallId, supportOperation = null }) {
    assertAiEnabled(); // AI-4 audit 2026-04-21: pilot-day kill switch
    validateModel(model);
    const governedSupportRoute = supportOperation
        ? _governedSupportRouteForGateway(signal, supportOperation)
        : null;
    if (governedSupportRoute) {
        model = governedSupportRoute.model;
        temperature = governedSupportRoute.capability.temperature;
        maxTokens = governedSupportRoute.capability.maxOutputTokens;
    }
    const governedRoute = governedSupportRoute ? null : _governedRouteForGateway(signal, model);
    const governedGatewayRoute = governedRoute || governedSupportRoute;
    const availabilityRoute = governedGatewayRoute
        ? { requestedModel: model, effectiveModel: model, availabilityRerouted: false }
        : applyModelAvailabilityLane(task, pipelineId, model);
    model = availabilityRoute.effectiveModel;
    if (!Number.isFinite(maxTokens) || maxTokens <= 0) {
        maxTokens = getMaxOutputTokens(model);
    }
    const modelRouteAudit = buildModelRouteAudit(task, model, pipelineId, availabilityRoute, modelRouteAttempt);
    const evalAttempt = beginEvalAiLogicalStage({ pipelineId, stageName, task, model, logicalCallId });

    if (!SecurityUtils.rateLimiter.canMakeRequest('api')) {
        throw new Error('Rate limit exceeded. Please wait a moment before making more requests.');
    }

    // WO-C: route through backend gateway in default mode. The backend
    // attaches the provider key, enforces policy, and records the ledger
    // row. In hybrid mode, we try backend first and fall back to the direct
    // browser path on network failure (dev offline scenario).
    if (governedGatewayRoute || _isBackendGatewayActive()) {
        try {
            const feature = _pipelineToFeature(pipelineId, stageName);
            const monitorId = PipelineMonitor.start(pipelineId || 'api-call', stageName || 'unknown', {
                model, temperature, maxTokens,
                task,
                modelRoute: modelRouteAudit,
                promptPreview: previewContent(messages?.[0]?.content, 200),
                promptFull: messages?.[0]?.content,
                gateway: 'backend',
                originator,
            }, projectId);
            try {
                const { DataServiceAPI } = await import('./data-service-api');
                const gatewayTimeouts = _resolveGatewayTimeouts(timeoutMs);
                // JSON-mode and truncation recovery can issue a second backend
                // request. Give each provider attempt its own server deadline
                // and transport cushion; reusing the first attempt's timer
                // would let the browser abort a legitimate second attempt
                // while its backend provider call continued running.
                // The slot is taken BEFORE the transport timer starts, so time
                // spent queued behind other calls is never counted against a
                // request that has not been sent (ai-gateway-limiter.ts).
                const callGateway = (payload) => withAiGatewaySlot(async () => {
                    const { signal: requestSignal, cleanup, didClientTimeout } = _makeRequestSignal(
                        signal,
                        gatewayTimeouts.clientTransportTimeoutMs,
                    );
                    try {
                        return await withEvalAiPaidRequest(evalAttempt, {
                            route: 'backend-gateway-chat',
                            task,
                            model,
                            requestPayload: payload,
                        }, async (evaluationRequestPermit) => {
                            const data = await DataServiceAPI.aiGateway.chat({
                                ...payload,
                                timeoutMs: gatewayTimeouts.providerTimeoutMs,
                                signal: requestSignal,
                                evaluationRequestPermit,
                            });
                            _assertEvalBackendSingleAttemptProof(
                                evalAttempt,
                                data?.evaluationSingleAttempt,
                                'backend-gateway-chat',
                            );
                            return data;
                        });
                    } catch (innerErr) {
                        _markClientTimeoutSource(innerErr, didClientTimeout);
                        throw innerErr;
                    } finally {
                        cleanup();
                    }
                }, {
                    signal,
                    // An evaluation must prove one paid attempt. Background
                    // pre-compute is deferred at once when the provider is busy
                    // (it re-fires on the next scope edit) and must never sit in
                    // a wait. Both still trigger the pause that protects the rest.
                    retryOnBackpressure: !evalAttempt
                        && !String(stageName || '').toLowerCase().includes('background')
                        && String(pipelineId || '') !== 'rationale-precompute',
                });
                const gatewayPayload = {
                    feature, task, model, messages, temperature, maxTokens,
                    responseFormat,
                    projectId,
                    ...(evalAttempt ? { evaluationSingleAttempt: true } : {}),
                    metadata: buildModelRouteLedgerMetadata(modelRouteAudit),
                    ..._governedGatewayFields(governedRoute),
                    ..._governedSupportGatewayFields(governedSupportRoute),
                };
                let data = await callGateway(gatewayPayload);
                _bindGovernedRoutingReceipt(
                    governedGatewayRoute,
                    signal,
                    data?.governedRoutingReceipt,
                    'backend-gateway-chat',
                );
                if (!governedGatewayRoute && !hasUsableAiContent(data?.content) && responseFormat) {
                    // Some upstream model/provider combinations accept the
                    // request but return an empty assistant message when
                    // OpenAI JSON mode is present. Retry once without the
                    // hint before recording a failed monitor entry.
                    if (!suppressEvalAiRetry('empty_json_retry', {
                        handle: evalAttempt,
                        route: 'backend-gateway-chat',
                        task,
                        model,
                        reason: 'empty_json_mode_response',
                    })) {
                        data = await callGateway({
                            ...gatewayPayload,
                            responseFormat: undefined,
                            metadata: {
                                ...buildModelRouteLedgerMetadata(modelRouteAudit),
                                retryReason: 'empty_json_mode_response',
                                originalResponseFormat: responseFormat?.type || 'unknown',
                            },
                        });
                    }
                } else if (
                    !governedGatewayRoute
                    &&
                    data?.finishReason === 'length'
                    && responseFormat
                ) {
                    // Slice B (v5.46.45): the model hit its output cap, so the
                    // JSON is truncated/broken. Retry ONCE at the model's real
                    // ceiling when we asked for less; if we were already at the
                    // ceiling the post-call guard surfaces a clear AI_TRUNCATED
                    // error instead of feeding broken JSON to parseJsonSafe.
                    const ceiling = resolveModelOutputCeiling(model);
                    const retryEligible = ceiling && Number.isFinite(maxTokens) && maxTokens < ceiling;
                    const retrySuppressed = retryEligible && suppressEvalAiRetry('truncation_retry', {
                        handle: evalAttempt,
                        route: 'backend-gateway-chat',
                        task,
                        model,
                        reason: 'finish_reason_length',
                    });
                    if (retryEligible && !retrySuppressed && !_truncationRetryDisabled()) {
                        data = await callGateway({
                            ...gatewayPayload,
                            maxTokens: ceiling,
                            metadata: {
                                ...buildModelRouteLedgerMetadata(modelRouteAudit),
                                retryReason: 'truncated_length',
                                priorMaxTokens: maxTokens,
                                retryMaxTokens: ceiling,
                            },
                        });
                    }
                }
                const content = data?.content || '';
                const usage = data?.usage || {};
                const finishReason = data?.finishReason || null;
                const provider = data?.provider || 'openrouter';
                const providerRoute = data?.providerRoute || {
                    requestedProvider: provider,
                    effectiveProvider: provider,
                    source: 'backend-gateway',
                    fallbackApplied: false,
                };
                const providerFallback = data?.providerFallback || null;
                if (!hasUsableAiContent(content)) {
                    const err = new Error(describeInvalidAiResponse(data, { model, pipelineId, stageName }));
                    err.code = 'AI_EMPTY_CONTENT';
                    // Carried as fields, not only inside the message, so the
                    // queue can tell "the provider failed" from "the model
                    // chose to say nothing" (ai-service-failure.ts).
                    err.finishReason = finishReason;
                    err.tokensIn = usage.tokensIn ?? usage.prompt_tokens ?? null;
                    err.tokensOut = usage.tokensOut ?? usage.completion_tokens ?? null;
                    PipelineMonitor.error(monitorId, err, {
                        model,
                        finishReason,
                        tokensIn: usage.tokensIn,
                        tokensOut: usage.tokensOut,
                        provider,
                        providerRoute,
                        providerFallback,
                    });
                    err.body = { provider, providerRoute, providerFallback };
                    throw err;
                }
                // A response stopped by the output ceiling is incomplete whether
                // it is JSON, prose or a tool plan. The kill switch may suppress
                // the optional paid retry, but it must never turn partial output
                // into a successful application result.
                if (finishReason === 'length') {
                    const err = createTruncatedOutputError(pipelineId || 'response', usage);
                    err.body = { provider, providerRoute, providerFallback };
                    throw err;
                }
                // Pass the complete provider response to PipelineMonitor. The
                // monitor owns its separate 500-char scan preview and bounded
                // full-response copy; truncating here made Developer Tools
                // falsely look as though the AI itself had a 500-char limit.
                PipelineMonitor.success(monitorId, content, {
                    model,
                    tokensIn: usage.tokensIn,
                    tokensOut: usage.tokensOut,
                    cacheCreationTokens: usage.cacheCreationTokens,
                    cacheReadTokens: usage.cacheReadTokens,
                    reasoningTokens: usage.reasoningTokens,
                    costUsd: usage.costUsd,
                    finishReason,
                    truncated: finishReason === 'length',
                    gateway: 'backend',
                    provider,
                    providerRoute,
                    providerFallback,
                });
                return content;
            } catch (err) {
                const abortInfo = classifyAbortError(err);
                if (abortInfo) {
                    PipelineMonitor.error(monitorId, new Error(abortInfo.label), buildProviderFailureMonitorMeta(
                        err,
                        model,
                        { cancelled: abortInfo.cancelled },
                        { provider: 'openrouter', source: 'backend-gateway' },
                    ));
                    throw err;
                }
                // Background pre-compute (rationale-precompute, etc) hitting a
                // transient AI-provider state (keys quarantined, rate-limited,
                // upstream 5xx) is expected during warmup and during a brief
                // post-error cooldown. The JS catch in the caller silently
                // swallows it and the next scope edit will re-fire — surfacing
                // a scary red row in the Pipeline Monitor mis-attributes a
                // benign condition. Downgrade to cancelled with a clear label.
                const isBackgroundPrecompute = String(stageName || '').toLowerCase().includes('background')
                    || String(pipelineId || '') === 'rationale-precompute';
                const transientCode = err?.code === 'all_keys_quarantined'
                    || err?.code === 'upstream_429'
                    || err?.code === 'upstream_provider_rate_limited'
                    || (Number(err?.upstreamStatusCode) >= 500);
                if (isBackgroundPrecompute && transientCode) {
                    const label = err?.code === 'all_keys_quarantined'
                        ? 'Deferred — AI provider warming'
                        : 'Deferred — provider transient';
                    PipelineMonitor.error(monitorId, new Error(label), buildProviderFailureMonitorMeta(
                        err,
                        model,
                        { cancelled: true },
                        { provider: 'openrouter', source: 'backend-gateway' },
                    ));
                    throw err;
                }
                // Hybrid mode: try legacy browser path on gateway miss
                if (governedGatewayRoute) throw err;
                if (getAiGatewayMode() === 'hybrid') {
                    PipelineMonitor.error(monitorId, err, buildProviderFailureMonitorMeta(
                        err,
                        model,
                        { gatewayFallback: 'browser-direct' },
                        { provider: 'openrouter', source: 'backend-gateway' },
                    ));
                    // fall through to legacy path below
                } else {
                    PipelineMonitor.error(monitorId, err, buildProviderFailureMonitorMeta(
                        err,
                        model,
                        {},
                        { provider: 'openrouter', source: 'backend-gateway' },
                    ));
                    throw err;
                }
            }
        } catch (err) {
            if (classifyAbortError(err)) throw err;
            if (governedGatewayRoute) throw err;
            if (getAiGatewayMode() !== 'hybrid') throw err;
            if (suppressEvalAiRetry('hybrid_gateway_fallback', {
                handle: evalAttempt,
                route: 'backend-to-browser-direct',
                task,
                model,
                reason: err?.message || 'backend_gateway_failure',
            })) throw err;
            // else fall through to browser path
        }
    }

    assertBrowserOpenRouterAllowed();

    // Resolve the effective primary key for validation. Provider storage
    // takes precedence; otherwise we fall back to the legacy `apiKey` arg.
    const effectiveKey = getKeyForProvider(inferProviderForModel(model))
        || getKeyForProvider('openrouter')
        || apiKey;
    if (!SecurityUtils.isValidApiKey(effectiveKey)) {
        throw new Error('Invalid API key format');
    }

    const monitorId = PipelineMonitor.start(pipelineId || 'api-call', stageName || 'unknown', {
        model, temperature, maxTokens,
        task,
        modelRoute: modelRouteAudit,
        promptPreview: previewContent(messages?.[0]?.content, 200),
        promptFull: messages?.[0]?.content,
        originator,
    }, projectId);

    try {
        const { content, usage, finishReason, provider, providerRoute, providerFallback } = await callWithFailover({
            apiKey, model, messages, temperature, maxTokens, signal, responseFormat, task, timeoutMs,
            evalAttempt,
        });

        if (!content) {
            const err = new Error('Invalid response from API');
            err.body = { provider, providerRoute, providerFallback };
            PipelineMonitor.error(monitorId, err, {
                model,
                provider,
                providerRoute,
                providerFallback,
            });
            throw err;
        }

        if (finishReason === 'length') {
            const err = createTruncatedOutputError(pipelineId || 'response', usage);
            err.body = { provider, providerRoute, providerFallback };
            throw err;
        }

        PipelineMonitor.success(monitorId, content, {
            model,
            tokensIn: usage?.tokensIn,
            tokensOut: usage?.tokensOut,
            // Workstream E2. Cache + reasoning buckets and OpenRouter cost.
            // parseUsage in ai-providers.js populates these; they're undefined
            // on older / non-Anthropic provider responses, which is fine.
            cacheCreationTokens: usage?.cacheCreationTokens,
            cacheReadTokens: usage?.cacheReadTokens,
            reasoningTokens: usage?.reasoningTokens,
            costUsd: usage?.costUsd,
            finishReason,
            truncated: finishReason === 'length',
            provider,
            providerRoute,
            providerFallback,
        });
        return content;
    } catch (err) {
        // Distinguish genuine user cancel (AbortError) from client-side fetch
        // timeout (TimeoutError). Both reach here via a thrown DOMException,
        // but the monitor should not label a timeout as "Cancelled by user".
        const abortInfo = classifyAbortError(err);
        if (abortInfo) {
            PipelineMonitor.error(monitorId, new Error(abortInfo.label), buildProviderFailureMonitorMeta(err, model, { cancelled: abortInfo.cancelled }));
        } else {
            PipelineMonitor.error(monitorId, err, buildProviderFailureMonitorMeta(err, model));
        }
        throw err;
    }
}

/**
 * Call OpenRouter with tool_use support (OpenAI-compatible function calling).
 *
 * Returns { content, toolCalls, usage } where toolCalls is an array of
 * { id, type, function: { name, arguments } } objects, or null if the model
 * responded with plain text.
 *
 * Uses the same multi-key rotation and PipelineMonitor integration as
 * callOpenRouter. Callers are responsible for executing tool calls and
 * re-calling with tool results appended to the messages array (agent loop).
 *
 * @param {Object} args
 * @param {string} args.apiKey
 * @param {string} args.model
 * @param {Array}  args.messages          OpenAI-format message array
 * @param {Array}  args.tools             Tool definitions [{type:'function', function:{name,description,parameters}}]
 * @param {string|Object} [args.tool_choice] 'auto'|'none'|'required'|{type:'function',function:{name}}
 * @param {number} [args.temperature=0.2]
 * @param {number} [args.maxTokens] Omit to use the routed model's verified ceiling.
 * @param {string} [args.pipelineId]
 * @param {string} [args.stageName]
 * @param {string} [args.projectId]
 * @param {AbortSignal} [args.signal]
 * @param {Object} [args.modelRouteAttempt] Explicit runtime-attempt audit (assistant transient fallback)
 * @returns {Promise<{content: string|null, toolCalls: Array|null, usage: Object, provider?: string, providerRoute?: Object, providerFallback?: Object|null}>}
 */
export async function callOpenRouterWithTools({ apiKey, model, messages, tools, tool_choice, temperature = 0.2, maxTokens = /** @type {number | null} */ (null), pipelineId, stageName, projectId, signal, task, timeoutMs, modelRouteAttempt }) {
    assertAiEnabled(); // AI-4 audit 2026-04-21: pilot-day kill switch
    validateModel(model);
    const governedRoute = _governedRouteForGateway(signal, model);
    const availabilityRoute = governedRoute
        ? { requestedModel: model, effectiveModel: model, availabilityRerouted: false }
        : applyModelAvailabilityLane(task, pipelineId, model);
    model = availabilityRoute.effectiveModel;
    if (!Number.isFinite(maxTokens) || maxTokens <= 0) {
        maxTokens = getMaxOutputTokens(model);
    }
    const modelRouteAudit = buildModelRouteAudit(task, model, pipelineId, availabilityRoute, modelRouteAttempt);
    const evalAttempt = beginEvalAiLogicalStage({ pipelineId, stageName, task, model });

    if (!SecurityUtils.rateLimiter.canMakeRequest('api')) {
        throw new Error('Rate limit exceeded. Please wait a moment before making more requests.');
    }

    // WO-C: backend gateway routing for tool-use calls.
    if (governedRoute || _isBackendGatewayActive()) {
        const feature = _pipelineToFeature(pipelineId, stageName);
        const lastMsg = messages?.[messages.length - 1];
        const previewText = typeof lastMsg?.content === 'string'
            ? lastMsg.content.substring(0, 200) : '(multi-part message)';
        const monitorId = PipelineMonitor.start(pipelineId || 'tool-call', stageName || 'unknown', {
            model, temperature, maxTokens,
            task,
            modelRoute: modelRouteAudit,
            toolCount: tools?.length || 0,
            promptPreview: previewText,
            promptFull: typeof lastMsg?.content === 'string' ? lastMsg.content : undefined,
            projectId,
            gateway: 'backend',
        }, projectId);
        try {
            const { DataServiceAPI } = await import('./data-service-api');
            const gatewayTimeouts = _resolveGatewayTimeouts(timeoutMs);
            // Slot first, transport timer second (ai-gateway-limiter.ts).
            const data = await withAiGatewaySlot(async () => {
            const { signal: requestSignal, cleanup, didClientTimeout } = _makeRequestSignal(
                signal,
                gatewayTimeouts.clientTransportTimeoutMs,
            );
            try {
                const paidRequest = {
                    feature, task, model, messages, temperature, maxTokens,
                    tools, toolChoice: tool_choice,
                    timeoutMs: gatewayTimeouts.providerTimeoutMs,
                    projectId,
                    endpoint: '/ai/chat-with-tools',
                    ...(evalAttempt ? { evaluationSingleAttempt: true } : {}),
                    metadata: buildModelRouteLedgerMetadata(modelRouteAudit),
                    ..._governedGatewayFields(governedRoute),
                };
                return await withEvalAiPaidRequest(evalAttempt, {
                    route: 'backend-gateway-tools',
                    task,
                    model,
                    requestPayload: paidRequest,
                }, async (evaluationRequestPermit) => {
                    const result = await DataServiceAPI.aiGateway.chat({
                        ...paidRequest,
                        signal: requestSignal,
                        evaluationRequestPermit,
                    });
                    _assertEvalBackendSingleAttemptProof(
                        evalAttempt,
                        result?.evaluationSingleAttempt,
                        'backend-gateway-tools',
                    );
                    return result;
                });
            } catch (innerErr) {
                _markClientTimeoutSource(innerErr, didClientTimeout);
                throw innerErr;
            } finally {
                cleanup();
            }
            }, { signal, retryOnBackpressure: !evalAttempt });
            _bindGovernedRoutingReceipt(
                governedRoute,
                signal,
                data?.governedRoutingReceipt,
                'backend-gateway-tools',
            );
            const content = data?.content || null;
            const toolCalls = data?.toolCalls || null;
            const annotations = Array.isArray(data?.annotations) ? data.annotations : [];
            const usage = data?.usage || {};
            const finishReason = data?.finishReason || null;
            const provider = data?.provider || 'openrouter';
            const providerRoute = data?.providerRoute || {
                requestedProvider: provider,
                effectiveProvider: provider,
                source: 'backend-gateway',
                fallbackApplied: false,
            };
            const hasToolCalls = Array.isArray(toolCalls) && toolCalls.length > 0;
            if (finishReason === 'length') {
                const err = createTruncatedOutputError(pipelineId || 'tool response', usage);
                err.body = { provider, providerRoute, providerFallback: data?.providerFallback || null };
                throw err;
            }
            const monitorOutput = content || (hasToolCalls
                ? `[tool_calls: ${toolCalls.map(tc => tc.function?.name).join(', ')}]`
                : '');
            PipelineMonitor.success(monitorId, monitorOutput, {
                model,
                tokensIn: usage.tokensIn,
                tokensOut: usage.tokensOut,
                cacheCreationTokens: usage.cacheCreationTokens,
                cacheReadTokens: usage.cacheReadTokens,
                reasoningTokens: usage.reasoningTokens,
                costUsd: usage.costUsd,
                toolCallCount: hasToolCalls ? toolCalls.length : 0,
                finishReason,
                truncated: finishReason === 'length',
                projectId,
                gateway: 'backend',
                provider,
                providerRoute,
                providerFallback: data?.providerFallback || null,
            });
            return {
                content,
                toolCalls: hasToolCalls ? toolCalls : null,
                annotations,
                usage,
                provider,
                providerRoute,
                providerFallback: data?.providerFallback || null,
            };
        } catch (err) {
            const abortInfo = classifyAbortError(err);
            if (abortInfo) {
                PipelineMonitor.error(monitorId, new Error(abortInfo.label), buildProviderFailureMonitorMeta(
                    err,
                    model,
                    { cancelled: abortInfo.cancelled },
                    { provider: 'openrouter', source: 'backend-gateway' },
                ));
                throw err;
            }
            if (governedRoute || getAiGatewayMode() !== 'hybrid') {
                PipelineMonitor.error(monitorId, err, buildProviderFailureMonitorMeta(
                    err,
                    model,
                    {},
                    { provider: 'openrouter', source: 'backend-gateway' },
                ));
                throw err;
            }
            if (suppressEvalAiRetry('hybrid_gateway_fallback', {
                handle: evalAttempt,
                route: 'backend-to-browser-direct-tools',
                task,
                model,
                reason: err?.message || 'backend_gateway_failure',
            })) throw err;
            // hybrid: fall through to legacy path
            PipelineMonitor.error(monitorId, err, buildProviderFailureMonitorMeta(
                err,
                model,
                { gatewayFallback: 'browser-direct' },
                { provider: 'openrouter', source: 'backend-gateway' },
            ));
        }
    }

    assertBrowserOpenRouterAllowed();

    const effectiveKey = getKeyForProvider(inferProviderForModel(model))
        || getKeyForProvider('openrouter')
        || apiKey;
    if (!SecurityUtils.isValidApiKey(effectiveKey)) {
        throw new Error('Invalid API key format');
    }

    // Derive a meaningful preview from the last message (often the user turn)
    const lastMsg = messages?.[messages.length - 1];
    const previewText = typeof lastMsg?.content === 'string'
        ? lastMsg.content.substring(0, 200)
        : '(multi-part message)';

    const monitorId = PipelineMonitor.start(pipelineId || 'tool-call', stageName || 'unknown', {
        model, temperature, maxTokens,
        task,
        modelRoute: modelRouteAudit,
        toolCount: tools?.length || 0,
        promptPreview: previewText,
        promptFull: typeof lastMsg?.content === 'string' ? lastMsg.content : undefined,
        projectId,
    }, projectId);

    try {
        const { content, usage, toolCalls, annotations = [], finishReason, provider, providerRoute, providerFallback } = await callWithFailover({
            apiKey, model, messages, temperature, maxTokens, tools, tool_choice, signal, task, timeoutMs,
            evalAttempt,
        });

        const hasToolCalls = Array.isArray(toolCalls) && toolCalls.length > 0;
        if (finishReason === 'length') {
            const err = createTruncatedOutputError(pipelineId || 'tool response', usage);
            err.body = { provider, providerRoute, providerFallback };
            throw err;
        }
        const monitorOutput = content || (hasToolCalls
            ? `[tool_calls: ${toolCalls.map(tc => tc.function?.name).join(', ')}]`
            : '');

        PipelineMonitor.success(monitorId, monitorOutput, {
            model,
            tokensIn: usage?.tokensIn,
            tokensOut: usage?.tokensOut,
            // Workstream E2
            cacheCreationTokens: usage?.cacheCreationTokens,
            cacheReadTokens: usage?.cacheReadTokens,
            reasoningTokens: usage?.reasoningTokens,
            costUsd: usage?.costUsd,
            toolCallCount: hasToolCalls ? toolCalls.length : 0,
            finishReason,
            truncated: finishReason === 'length',
            projectId,
            provider,
            providerRoute,
            providerFallback,
        });

        return {
            content: content || null,
            toolCalls: hasToolCalls ? toolCalls : null,
            annotations: Array.isArray(annotations) ? annotations : [],
            usage,
            provider,
            providerRoute,
            providerFallback,
        };
    } catch (err) {
        const abortInfo = classifyAbortError(err);
        if (abortInfo) {
            PipelineMonitor.error(monitorId, new Error(abortInfo.label), buildProviderFailureMonitorMeta(err, model, { cancelled: abortInfo.cancelled }));
        } else {
            PipelineMonitor.error(monitorId, err, buildProviderFailureMonitorMeta(err, model));
        }
        throw err;
    }
}

export async function callOpenRouterVision({ apiKey, model, prompt, images, temperature = 0.2, maxTokens = /** @type {number | null} */ (null), pipelineId, stageName, signal, task, timeoutMs, responseFormat, reasoningEffort, projectId, logicalCallId, supportOperation }) {
    assertAiEnabled(); // AI-4 audit 2026-04-21: pilot-day kill switch
    if (!SecurityUtils.rateLimiter.canMakeRequest('api')) {
        throw new Error('Rate limit exceeded. Please wait a moment before making more requests.');
    }
    validateModel(model);
    const governedSupportRoute = supportOperation
        ? _governedSupportRouteForGateway(signal, supportOperation)
        : null;
    if (governedSupportRoute) model = governedSupportRoute.model;
    const governedRoute = governedSupportRoute ? null : _governedRouteForGateway(signal, model);
    const governedGatewayRoute = governedRoute || governedSupportRoute;
    const gatewayTemperature = governedSupportRoute
        ? governedSupportRoute.capability.temperature
        : temperature;
    const availabilityRoute = governedGatewayRoute
        ? { requestedModel: model, effectiveModel: model, availabilityRerouted: false }
        : applyModelAvailabilityLane(task, pipelineId, model);
    model = availabilityRoute.effectiveModel;
    // Native-audio providers account for the instruction and audio inside one
    // context window. Do not copy a model's advertised completion ceiling into
    // `max_tokens`: Voxtral advertises 32,768 output tokens while its selected
    // endpoint has a 32,000-token total context, so even the 32-token fixed
    // transcription instruction makes that request impossible. OpenRouter's
    // chat contract permits omitting max_tokens; the provider then sizes the
    // completion from the actual remaining context. Governed support routes
    // retain their immutable, server-approved bounded profile.
    const useProviderNativeAudioBudget = !governedSupportRoute
        && isNativeAudioOnlyTranscriptionRequest(task, images);
    if (!governedSupportRoute && (!Number.isFinite(maxTokens) || maxTokens <= 0)) {
        maxTokens = getMaxOutputTokens(model);
    }
    const gatewayMaxTokens = governedSupportRoute
        ? governedSupportRoute.capability.maxOutputTokens
        : maxTokens;
    // The backend still receives the advertised ceiling for spend-policy
    // admission. Only the final provider payload omits it for canonical native
    // audio, so safety accounting is not weakened to the no-ceiling fallback.
    const providerMaxTokens = useProviderNativeAudioBudget ? null : gatewayMaxTokens;
    const modelRouteAudit = buildModelRouteAudit(task, model, pipelineId, availabilityRoute);
    const evalAttempt = beginEvalAiLogicalStage({ pipelineId, stageName, task, model, logicalCallId });
    if (!images?.length) throw new Error('No images provided');
    if (governedSupportRoute) {
        if (prompt !== governedSupportRoute.capability.instruction
            || images.length !== 1
            || typeof images[0] !== 'string'
            || !images[0].startsWith('data:audio/')) {
            const error = new Error('Governed audio transcription requires the fixed instruction and exactly one audio payload.');
            error.name = 'GovernedModelRouteMismatchError';
            error.code = 'QM_GOVERNED_MODEL_ROUTE_MISMATCH';
            throw error;
        }
    }

    // WO-C: backend gateway routing for vision calls.
    if (governedGatewayRoute || _isBackendGatewayActive()) {
        const feature = _pipelineToFeature(pipelineId, stageName);
        // Images/PDFs use image_url; audio uses OpenRouter's native
        // input_audio block (base64 bytes + format). Mirror buildVisionBody().
        const content = buildOpenRouterMediaContent(prompt, images);
        const messages = [{ role: 'user', content }];
        const monitorId = PipelineMonitor.start(pipelineId || 'vision-call', stageName || 'unknown', {
            model,
            temperature: gatewayTemperature,
            maxTokens: gatewayMaxTokens,
            providerMaxTokens,
            providerSizedOutput: useProviderNativeAudioBudget,
            imageCount: images.length,
            task,
            modelRoute: modelRouteAudit,
            promptPreview: prompt?.substring(0, 200),
            promptFull: prompt,
            projectId,
            gateway: 'backend',
        }, projectId);
        try {
            const { DataServiceAPI } = await import('./data-service-api');
            const gatewayTimeouts = _resolveGatewayTimeouts(timeoutMs);
            // Slot first, transport timer second (ai-gateway-limiter.ts). Photo
            // and PDF-page batches are most of one project's burst.
            const data = await withAiGatewaySlot(async () => {
            const { signal: requestSignal, cleanup, didClientTimeout } = _makeRequestSignal(
                signal,
                gatewayTimeouts.clientTransportTimeoutMs,
            );
            try {
                const paidRequest = {
                    feature, task, model, messages,
                    temperature: gatewayTemperature,
                    ...(Number.isFinite(gatewayMaxTokens) && gatewayMaxTokens > 0
                        ? { maxTokens: gatewayMaxTokens }
                        : {}),
                    responseFormat,
                    reasoningEffort: governedGatewayRoute?.reasoningEffort ?? reasoningEffort,
                    timeoutMs: gatewayTimeouts.providerTimeoutMs,
                    projectId, endpoint: '/ai/chat/vision',
                    ...(evalAttempt ? { evaluationSingleAttempt: true } : {}),
                    metadata: buildModelRouteLedgerMetadata(modelRouteAudit),
                    ..._governedGatewayFields(governedRoute),
                    ..._governedSupportGatewayFields(governedSupportRoute),
                };
                return await withEvalAiPaidRequest(evalAttempt, {
                    route: 'backend-gateway-vision',
                    task,
                    model,
                    requestPayload: paidRequest,
                }, async (evaluationRequestPermit) => {
                    const result = await DataServiceAPI.aiGateway.chat({
                        ...paidRequest,
                        signal: requestSignal,
                        evaluationRequestPermit,
                    });
                    _assertEvalBackendSingleAttemptProof(
                        evalAttempt,
                        result?.evaluationSingleAttempt,
                        'backend-gateway-vision',
                    );
                    return result;
                });
            } catch (innerErr) {
                _markClientTimeoutSource(innerErr, didClientTimeout);
                throw innerErr;
            } finally {
                cleanup();
            }
            }, { signal, retryOnBackpressure: !evalAttempt });
            _bindGovernedRoutingReceipt(
                governedGatewayRoute,
                signal,
                data?.governedRoutingReceipt,
                'backend-gateway-vision',
            );
            const out = data?.content || '';
            const usage = data?.usage || {};
            const finishReason = data?.finishReason || null;
            const provider = data?.provider || 'openrouter';
            if (finishReason === 'length') {
                throw createTruncatedOutputError(pipelineId || 'vision output', usage);
            }
            PipelineMonitor.success(monitorId, out, {
                model,
                tokensIn: usage.tokensIn,
                tokensOut: usage.tokensOut,
                cacheCreationTokens: usage.cacheCreationTokens,
                cacheReadTokens: usage.cacheReadTokens,
                reasoningTokens: usage.reasoningTokens,
                costUsd: usage.costUsd,
                finishReason,
                truncated: finishReason === 'length',
                gateway: 'backend',
                provider,
                providerRoute: data?.providerRoute || {
                    requestedProvider: provider,
                    effectiveProvider: provider,
                    source: 'backend-gateway',
                    fallbackApplied: false,
                },
                providerFallback: data?.providerFallback || null,
            });
            return out;
        } catch (err) {
            const abortInfo = classifyAbortError(err);
            if (abortInfo) {
                PipelineMonitor.error(monitorId, new Error(abortInfo.label), buildProviderFailureMonitorMeta(
                    err,
                    model,
                    { cancelled: abortInfo.cancelled },
                    { provider: 'openrouter', source: 'backend-gateway' },
                ));
                throw err;
            }
            if (governedGatewayRoute || getAiGatewayMode() !== 'hybrid') {
                PipelineMonitor.error(monitorId, err, buildProviderFailureMonitorMeta(
                    err,
                    model,
                    {},
                    { provider: 'openrouter', source: 'backend-gateway' },
                ));
                throw err;
            }
            if (suppressEvalAiRetry('hybrid_gateway_fallback', {
                handle: evalAttempt,
                route: 'backend-to-browser-direct-vision',
                task,
                model,
                reason: err?.message || 'backend_gateway_failure',
            })) throw err;
            PipelineMonitor.error(monitorId, err, buildProviderFailureMonitorMeta(
                err,
                model,
                { gatewayFallback: 'browser-direct' },
                { provider: 'openrouter', source: 'backend-gateway' },
            ));
        }
    }

    assertBrowserOpenRouterAllowed();

    const effectiveKey = getKeyForProvider(inferProviderForModel(model))
        || getKeyForProvider('openrouter')
        || apiKey;
    if (!SecurityUtils.isValidApiKey(effectiveKey)) {
        throw new Error('Invalid API key format');
    }

    const monitorId = PipelineMonitor.start(pipelineId || 'vision-call', stageName || 'unknown', {
        model,
        temperature,
        maxTokens: gatewayMaxTokens,
        providerMaxTokens,
        providerSizedOutput: useProviderNativeAudioBudget,
        imageCount: images.length,
        task,
        modelRoute: modelRouteAudit,
        promptPreview: prompt?.substring(0, 200),
        promptFull: prompt,
    }, projectId);

    try {
        const { content, usage, finishReason, provider, providerRoute, providerFallback } = await callWithFailover({
            apiKey, model, temperature, maxTokens: providerMaxTokens,
            isVision: true, prompt, images, signal, task, timeoutMs,
            responseFormat, reasoningEffort,
            evalAttempt,
        });

        if (finishReason === 'length') {
            throw createTruncatedOutputError(pipelineId || 'vision output', usage);
        }

        PipelineMonitor.success(monitorId, content || '', {
            model,
            tokensIn: usage?.tokensIn,
            tokensOut: usage?.tokensOut,
            // Workstream E2
            cacheCreationTokens: usage?.cacheCreationTokens,
            cacheReadTokens: usage?.cacheReadTokens,
            reasoningTokens: usage?.reasoningTokens,
            costUsd: usage?.costUsd,
            finishReason,
            truncated: finishReason === 'length',
            provider,
            providerRoute,
            providerFallback,
        });
        return content || '';
    } catch (err) {
        const abortInfo = classifyAbortError(err);
        if (abortInfo) {
            PipelineMonitor.error(monitorId, new Error(abortInfo.label), buildProviderFailureMonitorMeta(err, model, { cancelled: abortInfo.cancelled }));
        } else {
            PipelineMonitor.error(monitorId, err, buildProviderFailureMonitorMeta(err, model));
        }
        throw err;
    }
}

// ============================================
// STREAMING
// ============================================
// Production streaming is owned by the authenticated backend gateway. The
// browser-direct branch remains an explicit local-development escape hatch and
// talks only to OpenRouter; it never attempts an unauthenticated Bedrock proxy.
export async function callOpenRouterStreaming({ apiKey, model, messages, temperature = 0.2, maxTokens = /** @type {number | null} */ (null), responseFormat, onChunk, pipelineId, stageName, signal, task, timeoutMs, projectId }) {
    assertAiEnabled(); // AI-4 audit 2026-04-21: pilot-day kill switch
    validateModel(model);
    const governedRoute = _governedRouteForGateway(signal, model);
    const availabilityRoute = governedRoute
        ? { requestedModel: model, effectiveModel: model, availabilityRerouted: false }
        : applyModelAvailabilityLane(task, pipelineId, model);
    model = availabilityRoute.effectiveModel;
    if (!Number.isFinite(maxTokens) || maxTokens <= 0) {
        maxTokens = getMaxOutputTokens(model);
    }
    const modelRouteAudit = buildModelRouteAudit(task, model, pipelineId, availabilityRoute);
    const evalAttempt = beginEvalAiLogicalStage({ pipelineId, stageName, task, model });
    if (!SecurityUtils.rateLimiter.canMakeRequest('api')) {
        throw new Error('Rate limit exceeded. Please wait a moment before making more requests.');
    }

    // WO-C: backend gateway streaming. Routes to /api/ai/chat/stream which
    // emits OpenAI-compatible SSE chunks (same parser below).
    if (governedRoute || _isBackendGatewayActive()) {
        const feature = _pipelineToFeature(pipelineId, stageName);
        const monitorId = PipelineMonitor.start(pipelineId || 'streaming-call', stageName || 'unknown', {
            model, temperature, maxTokens, streaming: true,
            task,
            modelRoute: modelRouteAudit,
            promptPreview: previewContent(messages?.[0]?.content, 200),
            promptFull: messages?.[0]?.content,
            projectId,
            gateway: 'backend',
        }, projectId);
        try {
            const { DataServiceAPI } = await import('./data-service-api');
            const gatewayTimeouts = _resolveGatewayTimeouts(timeoutMs);
            const { signal: requestSignal, cleanup, didClientTimeout } = _makeRequestSignal(
                signal,
                gatewayTimeouts.clientTransportTimeoutMs,
            );
            try {
                const paidRequest = {
                    feature, model, messages, temperature, maxTokens, responseFormat, projectId, task,
                    timeoutMs: gatewayTimeouts.providerTimeoutMs,
                    ...(evalAttempt ? { evaluationSingleAttempt: true } : {}),
                    metadata: buildModelRouteLedgerMetadata(modelRouteAudit),
                    ..._governedGatewayFields(governedRoute),
                };
                const response = await withEvalAiPaidRequest(evalAttempt, {
                    route: 'backend-gateway-stream',
                    task,
                    model,
                    requestPayload: paidRequest,
                }, async (evaluationRequestPermit) => {
                    const result = await DataServiceAPI.aiGateway.stream({
                        ...paidRequest,
                        signal: requestSignal,
                        evaluationRequestPermit,
                    });
                    _assertEvalBackendSingleAttemptStreamProof(
                        evalAttempt,
                        result,
                        'backend-gateway-stream',
                    );
                    return result;
                });
                if (!response.ok || !response.body) {
                    const errData = await response.json().catch(() => ({}));
                    const err = new Error(errData.error?.message || errData.error || `Gateway stream error (${response.status})`);
                    err.status = response.status;
                    _hydrateGatewayError(err, errData);
                    throw _annotateProviderError(err, { provider: 'openrouter', source: 'backend-gateway-stream' });
                }

                _bindGovernedRoutingReceipt(
                    governedRoute,
                    signal,
                    _decodeProviderAuditHeader(response.headers?.get?.('x-qm-governed-routing-receipt')),
                    'backend-gateway-stream',
                );

                const providerAudit = _providerAuditFromStreamResponse(response);
                let result;
                try {
                    result = await parseSSEStream(response, onChunk, requestSignal, {
                        trustedGatewayMetadata: true,
                    });
                } catch (streamError) {
                    throw _annotateProviderError(streamError, providerAudit);
                }
                assertStreamingOutputComplete(result, pipelineId);
                const fullContent = result.content;
                const tokensIn = result.usage?.tokensIn || Math.ceil(contentLengthForTokens(messages?.[0]?.content) / 4);
                const tokensOut = result.usage?.tokensOut || (fullContent ? Math.ceil(fullContent.length / 4) : 0);
                PipelineMonitor.success(monitorId, fullContent, {
                    model, tokensIn, tokensOut,
                    cacheCreationTokens: result.usage?.cacheCreationTokens,
                    cacheReadTokens: result.usage?.cacheReadTokens,
                    reasoningTokens: result.usage?.reasoningTokens,
                    costUsd: result.usage?.costUsd,
                    gateway: 'backend',
                    ...providerAudit,
                });
                return fullContent;
            } catch (innerErr) {
                _markClientTimeoutSource(innerErr, didClientTimeout);
                throw innerErr;
            } finally {
                // Streaming transport ownership lasts through complete SSE
                // parsing; clearing at response headers would leave a hung
                // body unbounded in the browser.
                cleanup();
            }
        } catch (err) {
            const abortInfo = classifyAbortError(err);
            if (abortInfo) {
                PipelineMonitor.error(monitorId, new Error(abortInfo.label), buildProviderFailureMonitorMeta(
                    err,
                    model,
                    { cancelled: abortInfo.cancelled },
                    { provider: 'openrouter', source: 'backend-gateway-stream' },
                ));
                throw err;
            }
            if (err?.code === 'AI_TRUNCATED' || governedRoute || getAiGatewayMode() !== 'hybrid') {
                PipelineMonitor.error(monitorId, err, buildProviderFailureMonitorMeta(
                    err,
                    model,
                    {},
                    { provider: 'openrouter', source: 'backend-gateway-stream' },
                ));
                throw err;
            }
            if (suppressEvalAiRetry('hybrid_gateway_fallback', {
                handle: evalAttempt,
                route: 'backend-to-browser-direct-stream',
                task,
                model,
                reason: err?.message || 'backend_gateway_failure',
            })) throw err;
            PipelineMonitor.error(monitorId, err, buildProviderFailureMonitorMeta(
                err,
                model,
                { gatewayFallback: 'browser-direct' },
                { provider: 'openrouter', source: 'backend-gateway-stream' },
            ));
            // fall through to legacy path below
        }
    }

    assertBrowserOpenRouterAllowed();

    // Streaming: rotate across configured keys on 402 (spend cap) / 429 (rate
    // limit). Up to 5 attempts - one per key - so a single exhausted key
    // doesn't halt a live analysis. Mirrors the non-streaming failover loop
    // in callWithFailover.
    //
    // AI-8 audit 2026-04-21 (semaphore per-attempt):
    // Each iteration below calls `fetchWithRateLimitRetry`, which acquires the
    // concurrency slot on entry and ALWAYS releases it in its finally block
    // (see lines ~160 / ~188). That means a rate-limited key rotation does NOT
    // hold a semaphore slot across the attempt boundary: the slot is released
    // between attempts and other waiters can make forward progress. The slot
    // is re-acquired for the next attempt. No refactor needed; behaviour is
    // already correct for key rotation under the 5-slot global cap.
    const allKeys = getOpenRouterKeys();
    const configuredMaxAttempts = Math.max(1, Math.min(5, allKeys.length || 1));
    const maxAttempts = evalAiAttemptLimit(configuredMaxAttempts);

    const monitorId = PipelineMonitor.start(pipelineId || 'streaming-call', stageName || 'unknown', {
        model, temperature, maxTokens, streaming: true,
        task,
        modelRoute: modelRouteAudit,
        promptPreview: previewContent(messages?.[0]?.content, 200),
        promptFull: messages?.[0]?.content,
    }, projectId);

    const directProviderAudit = {
        provider: 'openrouter',
        providerRoute: {
            requestedProvider: 'openrouter',
            effectiveProvider: 'openrouter',
            source: getAiGatewayMode() === 'hybrid' ? 'hybrid-browser-direct-stream' : 'browser-direct-stream',
            fallbackApplied: false,
        },
        providerFallback: null,
    };
    let lastError = null;
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
        const streamKey = getNextOpenRouterKey() || apiKey;
        if (!streamKey || !SecurityUtils.isValidApiKey(streamKey)) {
            const err = _annotateProviderError(
                new Error('Invalid or missing OpenRouter API key. Add one in AI Models page.'),
                { provider: 'openrouter', source: directProviderAudit.providerRoute.source },
            );
            PipelineMonitor.error(monitorId, err, { model, ...directProviderAudit });
            throw err;
        }

        try {
            const serializedBody = JSON.stringify({
                ...OPENROUTER_PROVIDER.buildBody({
                    model,
                    messages,
                    temperature,
                    maxTokens,
                    responseFormat,
                    task,
                }),
                stream: true,
            });
            const response = await withEvalAiPaidRequest(evalAttempt, {
                route: 'browser-direct-stream',
                task,
                model,
                requestPayload: serializedBody,
            }, () => fetchWithRateLimitRetry(OPENROUTER_API_URL, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${streamKey}`,
                    'Content-Type': 'application/json',
                    'HTTP-Referer': window.location.origin,
                    'X-Title': 'QuoteMaker Platform'
                },
                body: serializedBody,
                signal,
            }, {
                label: 'streaming',
                ...(timeoutMs !== undefined ? { timeoutMs } : {}),
                evalAttempt,
                evalTask: task,
                evalModel: model,
            }));

            if (response.status === 429 || response.status === 402) {
                const cooldownMs = response.status === 402 ? 60 * 60 * 1000 : undefined;
                markKeyRateLimited(streamKey, cooldownMs);
                const label = response.status === 402 ? 'spend cap (402)' : 'rate limit (429)';
                console.warn(`[OpenRouter-stream] Key ${attempt + 1}/${maxAttempts} ${label}, rotating...`);
                lastError = _annotateProviderError(new Error(`OpenRouter ${label}`), {
                    provider: 'openrouter',
                    source: directProviderAudit.providerRoute.source,
                });
                if (attempt < maxAttempts - 1) continue;
                if (configuredMaxAttempts > maxAttempts) {
                    suppressEvalAiRetry('key_rotation', {
                        handle: evalAttempt,
                        route: 'browser-direct-stream',
                        task,
                        model,
                        reason: `provider_${response.status}`,
                    });
                }
                throw lastError;
            }

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                const err = new Error(
                    errorData.error?.message
                    || errorData.error
                    || errorData.message
                    || `OpenRouter stream error (${response.status})`,
                );
                err.status = response.status;
                _hydrateGatewayError(err, errorData);
                throw _annotateProviderError(err, {
                    provider: 'openrouter',
                    source: directProviderAudit.providerRoute.source,
                });
            }

            let streamError = false;
            let result;
            try {
                result = await parseSSEStream(response, onChunk, signal);
            } catch (parseErr) {
                throw parseErr;
            }
            assertStreamingOutputComplete(result, pipelineId);
            const fullContent = result.content;
            streamError = fullContent.length > 0 && result.usage.tokensIn === 0 && result.usage.tokensOut === 0;

            const tokensIn = result.usage?.tokensIn || Math.ceil(contentLengthForTokens(messages?.[0]?.content) / 4);
            const tokensOut = result.usage?.tokensOut || (fullContent ? Math.ceil(fullContent.length / 4) : 0);

            // Workstream E2: forward cache/reasoning/cost through the
            // streaming path. Safe even when streamUsage is synthetic (null
            // / undefined fields flow through as null).
            const streamMeta = {
                model, tokensIn, tokensOut,
                cacheCreationTokens: result.usage?.cacheCreationTokens,
                cacheReadTokens: result.usage?.cacheReadTokens,
                reasoningTokens: result.usage?.reasoningTokens,
                costUsd: result.usage?.costUsd,
                ...directProviderAudit,
            };
            if (streamError) {
                PipelineMonitor.success(monitorId, fullContent, { ...streamMeta, partial: true });
            } else {
                PipelineMonitor.success(monitorId, fullContent, streamMeta);
            }
            return fullContent;
        } catch (err) {
            const abortInfo = classifyAbortError(err);
            if (abortInfo) {
                PipelineMonitor.error(monitorId, new Error(abortInfo.label), {
                    model,
                    cancelled: abortInfo.cancelled,
                    ...directProviderAudit,
                });
                throw err;
            }
            lastError = err;
            // If it was NOT a rotatable rate-limit, don't loop - fail fast.
            if (!err.message?.includes('OpenRouter spend cap') && !err.message?.includes('OpenRouter rate limit')) {
                PipelineMonitor.error(monitorId, err, buildProviderFailureMonitorMeta(
                    err,
                    model,
                    {},
                    directProviderAudit,
                ));
                throw err;
            }
            if (attempt === maxAttempts - 1) {
                PipelineMonitor.error(monitorId, err, { model, ...directProviderAudit });
                throw err;
            }
        }
    }

    const exhausted = lastError || _annotateProviderError(new Error('All OpenRouter keys exhausted'), {
        provider: 'openrouter',
        source: directProviderAudit.providerRoute.source,
    });
    PipelineMonitor.error(monitorId, exhausted, { model, ...directProviderAudit });
    throw exhausted;
}

// ============================================================================
// EMBEDDINGS - semantic retrieval foundation (M1)
// ----------------------------------------------------------------------------
// OpenRouter exposes an OpenAI-compatible /embeddings endpoint. We reuse the
// same multi-key rotation + 429 cooldown + withRetry machinery as chat calls
// so the embedding layer inherits all the resilience work already shipped.
//
// Default model: qwen/qwen3-embedding-8b - the most popular embedding model
// on OpenRouter as of Oct 2025. 32k input context (60% bigger than Gemini's
// 20k), $0.01/M tokens (15× cheaper than Gemini), native 4096-dim vectors
// with MRL (Matryoshka Representation Learning) support - you can truncate
// the vector to any smaller size (32–4096) and retain most semantic quality.
// Strong on multilingual, long-text, and retrieval / clustering tasks.
// Default dimensions left at model native (4096) for max retrieval quality;
// caller can override via `dimensions` arg. To swap the model entirely, set
// localStorage 'qm_embedding_model' (AI Models page surfaces this selector
// in a later M1 step).
//
// Usage:
//   const vec = await callEmbedding({ input: "waterproofing strata balcony" });
//   // vec === [0.0123, -0.0045, ...] (4096 dims, Qwen3 default)
//
//   const vec = await callEmbedding({ input: "...", dimensions: 1024 });
//   // vec === 1024-dim Matryoshka-reduced vector, same semantic direction
//
//   const vecs = await callEmbedding({ input: ["foo", "bar", "baz"] });
//   // vecs === [[...], [...], [...]] (batch of 3)
//
// The function returns number[] for single-string input, number[][] for
// array input - matching the OpenAI shape so no caller-side unwrapping
// is required.
// ============================================================================

const OPENROUTER_EMBEDDINGS_ENDPOINT = 'https://openrouter.ai/api/v1/embeddings';
export const DEFAULT_EMBEDDING_MODEL = 'qwen/qwen3-embedding-8b';
const EMBEDDING_MODEL_STORAGE_KEY = 'qm_embedding_model';
const EMBEDDING_DIMENSIONS_STORAGE_KEY = 'qm_embedding_dimensions';

/**
 * Resolve the configured embedding model ID.
 * Priority: explicit arg → localStorage override → built-in default.
 */
function resolveEmbeddingModel(override) {
    if (override && typeof override === 'string' && override.trim()) return override.trim();
    if (typeof window !== 'undefined') {
        try {
            const stored = localStorage.getItem(EMBEDDING_MODEL_STORAGE_KEY);
            if (stored && stored.trim()) return stored.trim();
        } catch { /* localStorage unavailable */ }
    }
    return DEFAULT_EMBEDDING_MODEL;
}

/**
 * Resolve the configured embedding dimensions.
 * Priority: explicit arg → localStorage override → model native (null).
 * Returning null means "use the model's native dimensionality" and we omit
 * the `dimensions` field from the request body entirely.
 */
function resolveEmbeddingDimensions(override) {
    if (Number.isFinite(override) && override > 0) return Math.floor(override);
    if (typeof window !== 'undefined') {
        try {
            const stored = parseInt(localStorage.getItem(EMBEDDING_DIMENSIONS_STORAGE_KEY) || '', 10);
            if (Number.isFinite(stored) && stored > 0) return stored;
        } catch { /* localStorage unavailable */ }
    }
    return null;
}

/**
 * Compute an embedding (or batch of embeddings) via OpenRouter.
 *
 * @param {Object} opts
 * @param {string|string[]} opts.input     Text or array of texts to embed
 * @param {string} [opts.model]            Embedding model ID (defaults to resolveEmbeddingModel())
 * @param {number} [opts.dimensions]       Truncate output to N dimensions (Gemini Matryoshka).
 *                                         Omit to use the model's native dimensionality.
 * @param {string} [opts.apiKey]           Override key (otherwise multi-key rotation picks)
 * @param {AbortSignal} [opts.signal]      Abort signal for cancellation
 * @returns {Promise<number[] | number[][]>} Single vector for string input, batch for array input
 */
export async function callEmbedding({ input, model, dimensions, apiKey, signal, pipelineId, stageName, logicalCallId } = {}) {
    assertAiEnabled(); // AI-4 audit 2026-04-21: pilot-day kill switch
    const governedSupportRoute = _governedSupportRouteForGateway(signal, 'embeddings');
    if (input == null || (typeof input === 'string' && input.trim() === '')) {
        throw new Error('callEmbedding: input is required (non-empty string or array of strings)');
    }
    const isBatch = Array.isArray(input);
    if (isBatch) {
        if (input.length === 0) throw new Error('callEmbedding: input array is empty');
        if (input.some(s => typeof s !== 'string' || s.trim() === '')) {
            throw new Error('callEmbedding: every input array element must be a non-empty string');
        }
    }

    const resolvedModel = governedSupportRoute?.model ?? resolveEmbeddingModel(model);
    const resolvedDims = governedSupportRoute
        ? governedSupportRoute.capability.embeddingDimensions
        : resolveEmbeddingDimensions(dimensions);
    if (governedSupportRoute) {
        const inputCount = Array.isArray(input) ? input.length : 1;
        if (inputCount > governedSupportRoute.capability.maxInputCount) {
            throw new Error('callEmbedding: governed input count exceeds the approved limit');
        }
        if (dimensions != null && dimensions !== resolvedDims) {
            const error = new Error('Governed embedding dimensions differ from the approved capability.');
            error.name = 'GovernedModelRouteMismatchError';
            error.code = 'QM_GOVERNED_MODEL_ROUTE_MISMATCH';
            throw error;
        }
    }
    const evalTask = 'embedding';
    const evalAttempt = beginEvalAiLogicalStage({
        pipelineId: pipelineId || 'embedding',
        stageName,
        task: evalTask,
        model: resolvedModel,
        logicalCallId,
    });

    // v5.13.14: backend gateway short-circuit. Mirrors callOpenRouter's
    // gateway-first routing so embedding traffic gets the same multi-key
    // rotator + ledger + tenant-policy enforcement. Pre-v5.13.14 this
    // helper hand-rolled an OpenRouter fetch and threw "No OpenRouter API
    // key configured" for end-users in default backend mode (semantic
    // retrieval was silently degraded to keyword-only matching).
    if (_isBackendGatewayActive()) {
        const feature = _pipelineToFeature(pipelineId, stageName) || 'embedding';
        try {
            const { DataServiceAPI } = await import('./data-service-api');
            const paidRequest = {
                feature,
                model: resolvedModel,
                input,
                dimensions: resolvedDims,
                ...(evalAttempt ? { evaluationSingleAttempt: true } : {}),
                ..._governedSupportGatewayFields(governedSupportRoute),
            };
            const data = await withEvalAiPaidRequest(evalAttempt, {
                route: 'backend-gateway-embedding',
                task: evalTask,
                model: resolvedModel,
                requestPayload: paidRequest,
            }, async (evaluationRequestPermit) => {
                const result = await DataServiceAPI.aiGateway.embeddings({
                    ...paidRequest,
                    signal,
                    evaluationRequestPermit,
                });
                _assertEvalBackendSingleAttemptProof(
                    evalAttempt,
                    result?.evaluationSingleAttempt,
                    'backend-gateway-embedding',
                );
                return result;
            });
            _bindGovernedRoutingReceipt(
                governedSupportRoute,
                signal,
                data?.governedRoutingReceipt,
                'backend-gateway-embedding',
            );
            const embeddings = Array.isArray(data?.embeddings) ? data.embeddings : [];
            if (embeddings.length === 0) {
                throw new Error('Empty embedding response from gateway');
            }
            return isBatch ? embeddings : embeddings[0];
        } catch (err) {
            if (classifyAbortError(err)) throw err;
            // In default backend mode we do NOT fall through to the legacy
            // browser-direct path; the gateway IS the system. Re-throw so
            // callers (e.g. learning-retrieval) handle the failure mode
            // they're already coded for (returns hits=[] gracefully).
            if (governedSupportRoute || getAiGatewayMode() !== 'hybrid') throw err;
            if (suppressEvalAiRetry('hybrid_gateway_fallback', {
                handle: evalAttempt,
                route: 'backend-to-browser-direct-embedding',
                task: evalTask,
                model: resolvedModel,
                reason: err?.message || 'backend_gateway_failure',
            })) throw err;
            // Hybrid mode: fall through to legacy browser-direct path below.
        }
    }

    if (governedSupportRoute) {
        const error = new Error('Governed embedding support roles cannot use browser-direct transport.');
        error.name = 'GovernedModelRouteUnavailableError';
        error.code = 'QM_GOVERNED_MODEL_ROUTE_BACKEND_REQUIRED';
        throw error;
    }
    assertBrowserOpenRouterAllowed();

    const body = { model: resolvedModel, input };
    if (resolvedDims != null) body.dimensions = resolvedDims;

    const allKeys = getOpenRouterKeys();
    const configuredMaxAttempts = Math.max(1, Math.min(5, allKeys.length || 1));
    const maxAttempts = evalAiAttemptLimit(configuredMaxAttempts);
    let lastError = null;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
        const key = getNextOpenRouterKey() || apiKey;
        if (!key) {
            throw new Error('No OpenRouter API key configured. Add one in Settings / AI Models page.');
        }

        try {
            const data = await withRetry(async () => {
                const serializedBody = JSON.stringify(body);
                const response = await withEvalAiPaidRequest(evalAttempt, {
                    route: 'browser-direct-embedding',
                    task: evalTask,
                    model: resolvedModel,
                    requestPayload: serializedBody,
                }, () => fetchWithRateLimitRetry(OPENROUTER_EMBEDDINGS_ENDPOINT, {
                    method: 'POST',
                    headers: OPENROUTER_PROVIDER.headers(key),
                    body: serializedBody,
                    signal,
                }, {
                    label: 'embedding',
                    evalAttempt,
                    evalTask,
                    evalModel: resolvedModel,
                }));
                if (response.status === 429) {
                    markKeyRateLimited(key);
                    const err = new Error('OpenRouter embeddings rate limit (429) - rotating key');
                    err.isRateLimit = true;
                    throw err;
                }
                if (!response.ok) {
                    const errData = await response.json().catch(() => ({}));
                    const msg = errData.error?.message || errData.error || errData.message || 'OpenRouter embeddings error';
                    throw new Error(`${msg} (status ${response.status})`);
                }
                return response.json();
            }, {
                maxRetries: 1,
                baseDelay: 1500,
                retryableErrors: [500, 502, 503, 504],
                evalAttempt,
                evalTask,
                evalModel: resolvedModel,
            });

            // OpenAI-compatible shape: { data: [{ embedding: [...], index: 0 }, ...], usage: {...} }
            if (!data?.data || !Array.isArray(data.data)) {
                throw new Error('Malformed embeddings response - missing data array');
            }
            // Preserve caller-supplied order using the `index` field
            const sorted = data.data.slice().sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
            const vectors = sorted.map(d => d.embedding).filter(v => Array.isArray(v));
            if (vectors.length === 0) throw new Error('Malformed embeddings response - no vectors returned');

            // M2 AUDIT FIX - sanity-cap each vector's length. The
            // EMBEDDING_DIMENSIONS_MAX constant (8192) is an upper bound
            // that comfortably covers every mainstream embedding model.
            // A response with a 100k-element vector is either a
            // provider bug or a DoS vector - either way reject it
            // before it lands in Dexie / Postgres where it would
            // bloat storage and slow every similarity calculation.
            for (const v of vectors) {
                if (v.length > EMBEDDING_DIMENSIONS_MAX) {
                    throw new Error(`Embedding dimension ${v.length} exceeds sanity cap ${EMBEDDING_DIMENSIONS_MAX}`);
                }
                // Also guard against non-finite numbers - a single NaN
                // would corrupt every subsequent cosineSimilarity call.
                for (let i = 0; i < v.length; i++) {
                    if (!Number.isFinite(v[i])) {
                        throw new Error(`Embedding contains non-finite value at index ${i}`);
                    }
                }
            }

            return isBatch ? vectors : vectors[0];
        } catch (err) {
            lastError = err;
            if (classifyAbortError(err)) throw err;
            if (err.isRateLimit && attempt < maxAttempts - 1) {
                console.warn(`[Embedding] Key ${attempt + 1}/${maxAttempts} rate-limited, rotating...`);
                continue;
            }
            if (err.isRateLimit && configuredMaxAttempts > maxAttempts) {
                suppressEvalAiRetry('key_rotation', {
                    handle: evalAttempt,
                    route: 'browser-direct-embedding',
                    task: evalTask,
                    model: resolvedModel,
                    reason: err?.message || 'provider_key_unavailable',
                });
            }
            throw err;
        }
    }

    throw lastError || new Error('All OpenRouter keys failed for embedding call');
}

// ─── Text-to-Speech (2026-06-18) ────────────────────────────────────────────
// Browser-facing wrapper over the gateway /api/ai/tts route. Resolves the TTS
// model via the registry (summary-tts → Voxtral Mini TTS, pinned by
// strongRecommendation so a global chat model can't leak in) and returns a
// base64 WAV data URL ready for `new Audio(url)`. Results are cached in-memory
// by model|voice|text so replays + page re-mounts don't re-bill OpenRouter.
const _ttsDataUrlCache = new Map();

export async function synthesizeSpeech({ text, voice, model: explicitModel, feature = 'summary_tts', projectId = null, signal } = {}) {
    assertAiEnabled(); // pilot-day kill switch parity with the other AI calls
    _rejectUnsupportedGovernedSubcall(signal, 'text-to-speech');
    if (!text || typeof text !== 'string' || !text.trim()) {
        throw new Error('synthesizeSpeech: text is required (non-empty string)');
    }
    // Explicit model wins (e.g. the AI Models page auditioning a voice before
    // committing); otherwise resolve via the registry (honours per-task override).
    const model = explicitModel || pickModelForTask('summary-tts') || 'mistralai/voxtral-mini-tts-2603';
    const effectiveVoice = resolveTtsVoiceForModel(model, voice) || DEFAULT_TTS_VOICE;
    // Keep billing/audit attribution project-bound. Sharing a cached audio URL
    // across projects would make the later project's speech invisible in the
    // usage ledger even though the UI presented it as that project's result.
    const cacheKey = `${projectId || 'unattributed'}|${model}|${effectiveVoice}|${text}`;
    // The cache holds EITHER the resolved data URL (string) OR the in-flight
    // Promise. Caching the promise de-dupes concurrent callers — e.g. the Stage
    // 1.5 prefetch warming the cache while auto-play asks for the same audio —
    // into ONE network round-trip instead of two. `await` flattens either shape.
    if (_ttsDataUrlCache.has(cacheKey)) return _ttsDataUrlCache.get(cacheKey);
    const evalAttempt = beginEvalAiLogicalStage({
        pipelineId: feature,
        stageName: 'tts',
        task: 'summary-tts',
        model,
    });

    const fetchPromise = (async () => {
        const { DataServiceAPI } = await import('./data-service-api');
        const paidRequest = {
            feature,
            model,
            input: text,
            voice: effectiveVoice,
            projectId,
            ...(evalAttempt ? { evaluationSingleAttempt: true } : {}),
        };
        const data = await withEvalAiPaidRequest(evalAttempt, {
            route: 'backend-gateway-tts',
            task: 'summary-tts',
            model,
            requestPayload: paidRequest,
        }, async (evaluationRequestPermit) => {
            const result = await DataServiceAPI.aiGateway.tts({
                ...paidRequest,
                signal,
                evaluationRequestPermit,
            });
            _assertEvalBackendSingleAttemptProof(
                evalAttempt,
                result?.evaluationSingleAttempt,
                'backend-gateway-tts',
            );
            return result;
        });
        const url = data?.audioDataUrl || data?.audio_data_url || null;
        if (!url) throw new Error('TTS gateway returned no audio');
        return url;
    })();

    // Cap the cache (~1MB/entry) so a long session can't grow unbounded.
    if (_ttsDataUrlCache.size >= 12) {
        const firstKey = _ttsDataUrlCache.keys().next().value;
        if (firstKey) _ttsDataUrlCache.delete(firstKey);
    }
    _ttsDataUrlCache.set(cacheKey, fetchPromise);

    try {
        const url = await fetchPromise;
        // Settle the entry to the bare URL (unless it was evicted / replaced).
        if (_ttsDataUrlCache.get(cacheKey) === fetchPromise) _ttsDataUrlCache.set(cacheKey, url);
        return url;
    } catch (err) {
        // Never cache a failure — drop it so a later call can retry the network.
        if (_ttsDataUrlCache.get(cacheKey) === fetchPromise) _ttsDataUrlCache.delete(cacheKey);
        throw err;
    }
}

/**
 * Cosine similarity between two equal-length vectors.
 * Returns a number in [-1, 1]; 1 = identical direction, 0 = orthogonal, -1 = opposite.
 * Throws if inputs are invalid (different lengths, non-finite values, empty).
 */
export function cosineSimilarity(a, b) {
    if (!Array.isArray(a) || !Array.isArray(b)) throw new Error('cosineSimilarity: both inputs must be arrays');
    if (a.length === 0 || a.length !== b.length) throw new Error(`cosineSimilarity: length mismatch (${a.length} vs ${b.length})`);
    let dot = 0, magA = 0, magB = 0;
    for (let i = 0; i < a.length; i++) {
        const x = a[i], y = b[i];
        if (!isFinite(x) || !isFinite(y)) throw new Error(`cosineSimilarity: non-finite value at index ${i}`);
        dot += x * y;
        magA += x * x;
        magB += y * y;
    }
    const denom = Math.sqrt(magA) * Math.sqrt(magB);
    if (denom === 0) return 0;
    return dot / denom;
}

/** Read the currently-configured embedding model ID (for UI display). */
export function getEmbeddingModel() {
    return resolveEmbeddingModel();
}

/** Persist a new embedding model ID (called from the AI Models page selector). */
export function setEmbeddingModel(modelId) {
    if (typeof window === 'undefined') return;
    try {
        if (modelId && typeof modelId === 'string' && modelId.trim()) {
            localStorage.setItem(EMBEDDING_MODEL_STORAGE_KEY, modelId.trim());
        } else {
            localStorage.removeItem(EMBEDDING_MODEL_STORAGE_KEY);
        }
    } catch (e) {
        console.warn('[Embedding] Failed to persist model setting:', e?.message);
    }
}

/** Read the currently-configured output dimensionality (or null for model native). */
export function getEmbeddingDimensions() {
    return resolveEmbeddingDimensions();
}

// Sanity upper bound - no mainstream embedding model today exceeds 8192
// dimensions. A user typing 99999 by mistake is better caught here than
// failing mid-retrieval against the provider. Gemini tops at 3072, Qwen3
// at 4096, OpenAI large at 3072 - this cap comfortably covers the headroom.
const EMBEDDING_DIMENSIONS_MAX = 8192;

/** Persist an output dimensionality preference. Pass 0/null to clear. */
export function setEmbeddingDimensions(dims) {
    if (typeof window === 'undefined') return;
    try {
        if (Number.isFinite(dims) && dims > 0) {
            const floored = Math.floor(dims);
            if (floored > EMBEDDING_DIMENSIONS_MAX) {
                console.warn(`[Embedding] Requested dimensions ${floored} exceeds sanity cap ${EMBEDDING_DIMENSIONS_MAX}; ignoring.`);
                return;
            }
            localStorage.setItem(EMBEDDING_DIMENSIONS_STORAGE_KEY, String(floored));
        } else {
            localStorage.removeItem(EMBEDDING_DIMENSIONS_STORAGE_KEY);
        }
    } catch (e) {
        console.warn('[Embedding] Failed to persist dimensions setting:', e?.message);
    }
}
